import React from 'react';
import { socketService } from '@services/socketService';
import CorgeeStorageHelper, { type Conversation } from '@components/Globals/CorgeeStorageHelper';
import ConversationCardHeader from './ConversationCardHeader';
import ConversationCardGrayBar from './ConversationCardGrayBar';
import ConversationCardMetrics from './ConversationCardMetrics';
import ConversationCardReplies from './ConversationCardReplies';
import AttachmentsContent from './AttachmentsContent';
import ConversationCardAddReply from './ConversationCardAddReply';
import { ImagesGallery, type Attachment as GalleryAttachment } from '@components/ImagesGallery';
import '@assets/fonts/glyphs/material-icons.css';
import CustomFields from '@components/CustomFields/CustomFields';
import '@components/CustomFields/CustomFields.css';
import SummernoteCorgee from '@components/Globals/SummernoteCorgee';
import * as CorgeeHelper from '@components/utils/CorgeeHelper';
import { addTargetBlankToLinks } from '@lib/linkUtils';
import ConversationApi from './api/ConversationApi';
import { conversationBus } from '@/lib/conversationBus';
import { handleOriginalConversationLinkClick, handleLinkedCardClick } from '@/lib/urlUtils';
import './ConversationViewCard.css';
import EditDetailHistory from './EditHistory/EditDetailHistory';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import cloneDeep from 'lodash.clonedeep';
import $ from 'jquery';
import CorgeeModal from '@components/Globals/CorgeeModal';
import CustomModal from '@components/Globals/CustomModal';
import Creatable from 'react-select/creatable';
import MenuApi from './api/MenuApi';
import type {
  ConversationDetail,
  Reply,
  ConversationPhase,
  ReplyTab,
  EmbeddedImage,
} from '../../types/conversation';
import type { ApiConfig, AppConfig, CorgeeFunctions } from './types';
import type { StylesConfig } from 'react-select';
import { normalizeConversationId } from '@lib/conversationUrl';

// ==================== Types & Interfaces ====================

interface ConversationViewCardProps {
  conversation: ConversationDetail;
  api: ApiConfig;
  isInfowindowViewCard?: boolean;
  isConversationDetailsView?: boolean;
  SocketConnectionId?: string;
  ConfigMenus?: any;
  config?: AppConfig;
  userData?: any;
  corgeeFunctions?: CorgeeFunctions;
  selectedPhaseId?: number | null;
  replyToHighlight?: number | null;
  shouldUpdateHideReplies?: boolean;
  UpdateTab?: {
    Enable: boolean;
    ConversationPhaseId: number;
  };

  // Callbacks
  onUpdateConversation?: (
    conversationId: number,
    updates: Partial<ConversationDetail>,
    skipUpdate?: boolean,
    source?: string
  ) => void;
  onAddReply?: (conversationId: number, reply: Reply) => void;
  onUpdateReply?: (
    conversationId: number,
    replyId: number,
    updates: Partial<Reply>,
    totalFavorites?: number
  ) => void;
  onDeleteReply?: (conversationId: number, replyId: number) => void;
  onChangedLayers?: (params: any, isFromInfoWindow?: boolean) => void;
  onAddLayerToCard?: (
    e: React.MouseEvent | MouseEvent,
    layer: any,
    functions?: { onUpdateLayerToCard?: (layerId: number) => void }
  ) => void;
  onAddLayerToProject?: (
    e: React.MouseEvent | MouseEvent,
    layer: any,
    functions?: { onUpdateLayerToCard?: (layerId: number) => void }
  ) => void;
  onReloadConversation?: (conversationId: number) => void;
  onSubmitTime?: (conversation: ConversationDetail) => void;
  onStopUpdateTab?: () => void;
  restoreHideRepliesHighlight?: (status: boolean) => void;
  removeUpdateButton?: () => void;
  UpdateCustomFields?: (conversation: any) => void;
  UpdateCustomFieldsInternal?: (id: number, details: any) => void;
  getForceReloadDetailsFn?: (fn: () => void) => void;
  hideCustomFields?: boolean;
  isHomeView?: boolean;
  forceShowDetails?: boolean;
  hasUnreadNotification?: boolean;
  initialTabIndex?: number;
  onTabChange?: (index: number) => void;
}

interface RequestConfig {
  apiUrl: string;
  authToken: string;
  isNew: boolean;
  canEdit: boolean;
  canTemporary: boolean;
  showHeader: boolean;
  disableAutoSave: boolean;
  onChangedProject?: (conversationId: number, projectId: number) => void;
  onChangedSecurityGroups?: (conversationId: number, groups: any[]) => void;
  onChangedGroupLayers?: (conversationId: number, layers: any[]) => void;
  onChangeLayers?: (conversationId: number, layers: any[]) => void;
  addLayerToCard?: (conversationId: number, layer: any) => void;
  addLayerToProject?: (...args: any[]) => void;
  SocketConnectionId?: string;
  onLoadDataCallback?: () => void;
  onChangeOwners?: (conversationId: number, owners: any[]) => void;
  onDuplicateItem?: (e: React.MouseEvent, conversation: Conversation) => void;
  ConfigMenus?: any;
  update: boolean;
}

interface DeletePhase {
  title: string;
  deleteConversationId: number;
  deleteConversationPhaseId: number;
}

interface EditHistory {
  open: boolean;
  title: string;
  customField: any[];
  onSaveModal: any;
  onEditModal: any;
}

interface PhaseSelected {
  CoreTaskId: number | null;
  ConversationPhaseId: number;
  Title: string;
}

interface SelectOption {
  value: number | string;
  label: string;
  isEnable?: boolean;
}

interface ConversationViewCardState {
  isImportant: boolean | null;
  seeMoreReplies: boolean;
  // Ground truth from the replies list: true = the window covers the whole
  // thread the API returned; null = unknown yet (fall back to count compare)
  repliesFullyShown: boolean | null;
  seeMoreRepliesGrayBar: boolean;
  isLoadingMoreReplies: boolean;
  totalFavorites: number;
  totalReplies: number;
  ptotalFavorites: number;
  ptotalReplies: number;
  showCompleteCard: boolean;
  openRepost: boolean;
  itemRepost: any;
  requestConfig: RequestConfig;
  submitTime: boolean;
  textDetailsSection: string;
  showDetails: boolean;
  pageNumber: number;
  pageSize: number;
  isFromAddReply: boolean;

  // Body editing
  enableEditBody: boolean;
  isSavingBody: boolean;
  displayFullContent: boolean;
  body: string;
  bodyEdited?: string;
  showBodyIsRequired: boolean;
  textHeight: number;
  showSeeMoreLink: boolean;
  cardBodyheight: number | string;

  // Highlighting
  highlight: boolean;
  highlightHeader: boolean;
  highlightCard: boolean;
  highlightAttachment: boolean;
  reactSelfUpdate: boolean;
  highlightSM: boolean;
  highlightTabOriginal: boolean;
  highlightTab: boolean;

  // Images
  embeddedImages: EmbeddedImage[];
  selectedImageId: number;
  openGallery: boolean;

  // Replies
  replies?: Reply[];
  repliesPendingToHighlight: number[];
  linkInNewWindow: number[];

  // Custom fields
  customFieldObject: any;
  edithistory: EditHistory;

  // Phases
  firstLayerColor: string;
  HasPhases: boolean;
  Phases: ConversationPhase[];
  inputValue: string;
  inputIndex: number;
  openModal: boolean;
  deletePhase: DeletePhase;
  distance: number;
  editingPhase: boolean;
  phaseMenuOpen: boolean;
  validateInputValue: boolean;
  descriptionInputValue: string;
  top: number;
  left: number;
  phaseSelected: PhaseSelected;
  tabIndex: number;
  showReplies: boolean;
  enablePhases: boolean;
  showArrows: boolean;
  options: SelectOption[];
  cmbSelect: SelectOption;
  isTabSaving: boolean;
  isCreatingPhase: boolean;
  showConfirmationModal: boolean;
  updateMessageMoreReplies: boolean;
  selectedReplyTab: ReplyTab;
  metrics: any[];
  updatedBy?: string;
  convImportant?: boolean | null;
}

const getSelectStyles = (isDark: boolean): StylesConfig<SelectOption, boolean> => ({
  control: base => ({
    ...base,
    minHeight: 26,
    fontSize: 11,
    borderColor: isDark ? '#4a4c4c' : '#ccc',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    boxShadow: 'none',
    borderRadius: 4,
    '&:hover': { borderColor: isDark ? '#62accb' : '#aaa' },
    width: 170,
    height: 30,
  }),
  container: base => ({ ...base, width: 170 }),
  menu: base => ({
    ...base,
    fontSize: 11,
    zIndex: 999,
    borderRadius: 2,
    marginTop: 2,
    width: 170,
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
  }),
  menuPortal: base => ({ ...base, zIndex: 999 }),
  menuList: base => ({ ...base, padding: 0, width: 170 }),
  valueContainer: base => ({ ...base, padding: '2px 6px', gap: 2 }),
  indicatorsContainer: base => ({ ...base, height: 30 }),
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: base => ({
    ...base,
    padding: '0 6px',
    color: isDark ? '#6e6860' : '#999',
    cursor: 'pointer',
  }),
  dropdownIndicator: base => ({ ...base, padding: '0 6px', color: isDark ? '#6e6860' : '#999' }),
  multiValue: base => ({
    ...base,
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
    borderRadius: 3,
    margin: '1px 0px',
    padding: 2,
  }),
  multiValueLabel: base => ({
    ...base,
    fontSize: 11,
    color: isDark ? '#a9a093' : '#333',
    padding: '1px 4px',
  }),
  multiValueRemove: base => ({
    ...base,
    color: isDark ? '#6e6860' : '#888',
    padding: '0 3px',
    ':hover': {
      backgroundColor: isDark ? '#4a4c4c' : '#e8e8e8',
      color: isDark ? '#a9a093' : '#555',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor:
      state.isSelected || state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
    color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
    fontSize: 11,
    padding: '5px 10px',
    cursor: 'pointer',
    width: 170,
    ':active': { backgroundColor: '#508ca6', color: '#fff' },
  }),
});

// ==================== Component ====================

class ConversationViewCard extends React.Component<
  ConversationViewCardProps,
  ConversationViewCardState
>
{
  private positionScrollSorteable: number = 0;
  private positionScrollCardBody: number = 0;
  private positionScrollCore: number = 0;
  private waitTime: any = 0;
  private refs: any = {};
  // Cached value to avoid expensive DOM parsing on every render
  private cachedShowSeeMoreContent: boolean = false;
  private cachedBody: string | undefined = undefined;
  private cardBodyRef: HTMLDivElement | null = null;
  private cardBodyScrollLeftPinTarget: HTMLDivElement | null = null;
  private summernoteCorgeeElement: any = null;
  private unsubReplyAdded?: () => void;
  // ReplyIds this card just confirmed through its own handleAddReply, so the global
  // 'reply-added' bus listener below can recognize its own echo and skip it. That listener
  // otherwise falls back to checking props.conversation.Replies for the same ReplyId, but
  // props only reflect the confirmed reply after React re-renders — and this card emits the
  // bus event synchronously, in the same tick as calling handleAddReply, right before that
  // re-render happens. So the props check always misses on the very card that just added the
  // reply, double-counting totalReplies. Entries are pruned a few seconds after being added,
  // just enough to bridge that race.
  private recentlyConfirmedReplyIds = new Set<number>();
  private unsubLayerUpdated?: () => void;
  private highlightCardTimeout?: ReturnType<typeof setTimeout>;
  private highlightCardRafId?: number;
  private _seeMoreResizeObserver: ResizeObserver | null = null;
  private _lastObservedWidth: number = 0;
  private _hasRegisteredLayerListener: boolean = false;
  private _skipNextSelfPhasesCreated: boolean = false;
  private _phaseLoadingTimeout?: ReturnType<typeof setTimeout>;
  // ConfigValueId resolved once per phase creation, see resolveConfigValueIdPhase().
  private _configValueIdPhase: number | null = null;

  constructor(props: ConversationViewCardProps)
  {
    super(props);

    const pageSize = 10;
    const layers =
      props.conversation.ConversationLayers && props.conversation.ConversationLayers.length > 0
        ? props.conversation.ConversationLayers.filter(x => x.LayerId != 0)
        : [];

    this.state = {
      isImportant: props.conversation.IsImportant,
      seeMoreReplies: false,
      seeMoreRepliesGrayBar: false,
      repliesFullyShown: null,
      isLoadingMoreReplies: false,
      totalFavorites: props.conversation.TotalFavorites,
      totalReplies: props.conversation.TotalReplies,
      ptotalFavorites: 0,
      ptotalReplies: 0,
      showCompleteCard: false,
      openRepost: false,
      itemRepost: {},
      requestConfig: {
        apiUrl: props.api.url,
        authToken: props.api.token,
        isNew: !props.isConversationDetailsView && !props.conversation.ConversationId,
        canEdit: CorgeeStorageHelper.canEdit(),
        canTemporary: CorgeeStorageHelper.canTemporary(),
        showHeader: true,
        disableAutoSave: false,
        onChangedProject: props.corgeeFunctions?.onChangedProject,
        onChangedSecurityGroups: props.corgeeFunctions?.onChangedSecurityGroups,
        onChangedGroupLayers: props.corgeeFunctions?.onChangedGroupLayers,
        onChangeLayers: props.onChangedLayers as any,
        addLayerToCard: props.onAddLayerToCard as any,
        addLayerToProject: props.onAddLayerToProject as any,
        SocketConnectionId: props.SocketConnectionId,
        onLoadDataCallback: props.config?.onLoadDataCallback,
        onChangeOwners: props.corgeeFunctions?.onChangeOwners,
        onDuplicateItem: props.corgeeFunctions?.onDuplicateItem as any,
        ConfigMenus: props.ConfigMenus,
        update: false,
      },
      submitTime: false,
      textDetailsSection: 'Hide Details',
      showDetails: true,
      pageNumber: 1,
      pageSize: pageSize,
      isFromAddReply: false,

      // Body editing
      enableEditBody: false,
      isSavingBody: false,
      displayFullContent: false,
      body: props.conversation.Body,
      showBodyIsRequired: false,
      textHeight: 170,
      // Start with false, will be calculated in componentDidMount based on actual height
      showSeeMoreLink: false,
      cardBodyheight: window.innerHeight,

      // Highlighting
      highlight: false,
      highlightHeader: false,
      highlightCard: false,
      highlightAttachment: false,
      reactSelfUpdate: false,
      highlightSM: false,
      highlightTabOriginal: false,
      highlightTab: false,

      // Images
      embeddedImages: [],
      selectedImageId: 0,
      openGallery: false,

      // Replies
      repliesPendingToHighlight: [],
      linkInNewWindow: [],

      // Custom fields
      customFieldObject: {},
      edithistory: {
        open: false,
        title: '',
        customField: [],
        onSaveModal: null,
        onEditModal: null,
      },

      // Phases
      firstLayerColor:
        layers.length > 0
          ? layers[0]?.LayerColor
            ? layers[0]!.LayerColor
            : ((layers[0] as any)?.Color ?? '')
          : '',
      HasPhases: props.conversation.HasPhases,
      Phases: props.conversation.Phases || [],
      inputValue: '',
      inputIndex: 0,
      openModal: false,
      deletePhase: {
        title: '',
        deleteConversationId: 0,
        deleteConversationPhaseId: 0,
      },
      distance: 300,
      editingPhase: false,
      phaseMenuOpen: false,
      validateInputValue: false,
      descriptionInputValue: '',
      top: 0,
      left: 0,
      phaseSelected: {
        CoreTaskId: props.conversation.CoreTaskId,
        ConversationPhaseId: 0,
        Title: 'All Phases',
      },
      tabIndex: props.initialTabIndex ?? 0,
      showReplies: true,
      enablePhases: props.conversation.CoreTaskId != null ? true : false,
      showArrows: false,
      options: [],
      cmbSelect: {
        value: '',
        label: '',
      },
      isTabSaving: (props.initialTabIndex ?? 0) > 0,
      isCreatingPhase: false,
      showConfirmationModal: false,
      updateMessageMoreReplies: false,
      selectedReplyTab: 'all',
      metrics: [],
    };

    // Bind all methods
    this.onSeeMoreReplies = this.onSeeMoreReplies.bind(this);
    this.onUpdateTotalFavorites = this.onUpdateTotalFavorites.bind(this);
    this.onResetRepliesList = this.onResetRepliesList.bind(this);
    this.onAddRepost = this.onAddRepost.bind(this);
    this.setShowRepost = this.setShowRepost.bind(this);
    this.onUpdateReply = this.onUpdateReply.bind(this);
    this.setShowDetails = this.setShowDetails.bind(this);
    this.setIsFromAddReply = this.setIsFromAddReply.bind(this);

    // Body editing
    this.onEditBody = this.onEditBody.bind(this);
    this.onSaveEditBody = this.onSaveEditBody.bind(this);
    this.onChangeSummernote = this.onChangeSummernote.bind(this);
    this.onMarkAsImportantBody = this.onMarkAsImportantBody.bind(this);
    this.onUpdateTemporalImportant = this.onUpdateTemporalImportant.bind(this);
    this.onKeyDownEditor = this.onKeyDownEditor.bind(this);
    this.closeEditor = this.closeEditor.bind(this);
    this.onCancelEditBody = this.onCancelEditBody.bind(this);
    this.onSeeMoreContent = this.onSeeMoreContent.bind(this);
    this.getTagsInBody = this.getTagsInBody.bind(this);
    this.countImages = this.countImages.bind(this);
    this.openLinkOnNewWindow = this.openLinkOnNewWindow.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);

    // Highlighting
    this.highlightBody = this.highlightBody.bind(this);
    this.highlightHeader = this.highlightHeader.bind(this);
    this.highlightCard = this.highlightCard.bind(this);
    this.highlightAttachments = this.highlightAttachments.bind(this);
    this.handleEmbeddedImagesClick = this.handleEmbeddedImagesClick.bind(this);
    this.onCloseGallery = this.onCloseGallery.bind(this);
    this.highlightSeeMore = this.highlightSeeMore.bind(this);
    this.removePendingHighlight = this.removePendingHighlight.bind(this);
    this.onSubmitTime = this.onSubmitTime.bind(this);

    // Edit history
    this.onOpenEditHistory = this.onOpenEditHistory.bind(this);
    this.onCloseEditHistory = this.onCloseEditHistory.bind(this);
    this.activaDarkMode = this.activaDarkMode.bind(this);
    this.onOpenInfoWindow = this.onOpenInfoWindow.bind(this);
    this.updateCustomFieldsHistory = this.updateCustomFieldsHistory.bind(this);

    // Phases/Tabs
    this.handleChangeTabs = this.handleChangeTabs.bind(this);
    this.reloadReplies = this.reloadReplies.bind(this);
    this.handleAddReply = this.handleAddReply.bind(this);
    this.addnewPhase = this.addnewPhase.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.renamePhase = this.renamePhase.bind(this);
    this.removePhase = this.removePhase.bind(this);
    this.closeModalPhase = this.closeModalPhase.bind(this);
    this.openModalPhase = this.openModalPhase.bind(this);
    this.scrollLft = this.scrollLft.bind(this);
    this.scrollRight = this.scrollRight.bind(this);
    this.addtemporalphase = this.addtemporalphase.bind(this);
    this.onAddHasPhase = this.onAddHasPhase.bind(this);
    this.notificationUpdateTab = this.notificationUpdateTab.bind(this);
    this.deepEqual = this.deepEqual.bind(this);
    this.isObject = this.isObject.bind(this);
    this.onShowArrows = this.onShowArrows.bind(this);
    this.onChangeTitle = this.onChangeTitle.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.loadCatPhases = this.loadCatPhases.bind(this);
    this.handlePhasesUpdated = this.handlePhasesUpdated.bind(this);
    this.CreateNewOption = this.CreateNewOption.bind(this);
    this.setPositionMessage = this.setPositionMessage.bind(this);
    this.catchEventsTabs = this.catchEventsTabs.bind(this);
    this.createDefaultPhase = this.createDefaultPhase.bind(this);
    this.createPhase = this.createPhase.bind(this);
    this.resetSeeMoreReplies = this.resetSeeMoreReplies.bind(this);
    this.setLoadingMoreReplies = this.setLoadingMoreReplies.bind(this);
    this.countReplies = this.countReplies.bind(this);
    this.scrollSortable = this.scrollSortable.bind(this);
    this.scrollCardBody = this.scrollCardBody.bind(this);
    this.scrollLayerContainer = this.scrollLayerContainer.bind(this);
    this.scrollCore = this.scrollCore.bind(this);
    this.removeEventScroll = this.removeEventScroll.bind(this);
    this.onGetPhaseSelected = this.onGetPhaseSelected.bind(this);
    this.onHandleReplyTabSelected = this.onHandleReplyTabSelected.bind(this);
    this.updateLinkConversationsByView = this.updateLinkConversationsByView.bind(this);
    this.getClickHandler = this.getClickHandler.bind(this);
    this.disableChecklist = this.disableChecklist.bind(this);
  }

  // ==================== Phase Selection ====================

  onGetPhaseSelected(): number
  {
    const { conversation } = this.props;
    const infoConv = $("[data-react-conv='" + conversation.ConversationId + "']");
    let convPhaseId = 0;
    if (infoConv.length)
    {
      const phaseSelected = infoConv.find('.react-tabs__tab--selected');
      if (phaseSelected.length > 0)
      {
        convPhaseId = parseInt($(phaseSelected).attr('data-conversationphaseid') || '0');
      }
    }
    return convPhaseId;
  }

  // ==================== Scroll Events ====================

  removeEventScroll(tabsH: any): void
  {
    this.handleChange(null);
    $(tabsH).closest('.sortableDashboard').off('scroll', this.scrollSortable);
    $(tabsH).closest('.corgee_com_card-body-info').off('scroll', this.scrollCardBody);
    $(tabsH).closest('.layer-custom-fields-container').off('scroll', this.scrollLayerContainer);
    $(tabsH).closest('.core2').off('scroll', this.scrollCore);
    this.positionScrollSorteable = 0;
    this.positionScrollCardBody = 0;
    this.positionScrollCore = 0;
  }

  scrollSortable(): void
  {
    const tabsH = this.refs.tabsH;
    const newPositionScrollSortable = $(tabsH).closest('.sortableDashboard').scrollTop() || 0;
    if (this.positionScrollSorteable != newPositionScrollSortable)
    {
      this.removeEventScroll(tabsH);
    }
  }

  /**
   * The info-window card body is a horizontal scroll container (overflow-x: hidden
   * still scrolls, it only hides the scrollbar). Focusing a custom field near the
   * right edge makes the browser scroll it into view, shifting the whole fields
   * grid left with no scrollbar to bring it back - and scrollLeft stays clamped to
   * the overflow once the field closes. Pin it to 0 so the grid never shifts.
   */
  private pinCardBodyScrollLeft = (): void =>
  {
    if (this.cardBodyRef && this.cardBodyRef.scrollLeft !== 0)
    {
      this.cardBodyRef.scrollLeft = 0;
    }
  };

  private attachCardBodyScrollLeftPin(): void
  {
    if (!this.props.isInfowindowViewCard || !this.cardBodyRef) return;
    if (this.cardBodyScrollLeftPinTarget === this.cardBodyRef) return;

    this.detachCardBodyScrollLeftPin();
    this.cardBodyRef.addEventListener('scroll', this.pinCardBodyScrollLeft);
    this.cardBodyScrollLeftPinTarget = this.cardBodyRef;
  }

  private detachCardBodyScrollLeftPin(): void
  {
    if (this.cardBodyScrollLeftPinTarget)
    {
      this.cardBodyScrollLeftPinTarget.removeEventListener('scroll', this.pinCardBodyScrollLeft);
      this.cardBodyScrollLeftPinTarget = null;
    }
  }

  scrollCardBody(): void
  {
    const tabsH = this.refs.tabsH;
    const newPositionScrollCardBody =
      $(tabsH).closest('.corgee_com_card-body-info').scrollTop() || 0;
    if (this.positionScrollCardBody != newPositionScrollCardBody)
    {
      this.removeEventScroll(tabsH);
    }
  }

  scrollLayerContainer(): void
  {
    const tabsH = this.refs.tabsH;
    this.removeEventScroll(tabsH);
  }

  scrollCore(): void
  {
    const tabsH = this.refs.tabsH;
    const newPositionScrollCore = $(tabsH).closest('.core2').scrollTop() || 0;
    if (this.positionScrollCore != newPositionScrollCore)
    {
      this.removeEventScroll(tabsH);
    }
  }

  // ==================== Phase Management ====================

  CreateNewOption(option: SelectOption): void
  {
    if (option.label.trim().length == 0)
    {
      this.setPositionMessage();
      this.setState({
        validateInputValue: true,
        descriptionInputValue: "This field can't be empty",
      });
    }
    else
    {
      let phaseId: number;
      let selectoption: SelectOption;
      const options = this.state.options;

      if (options.some(x => x.label == option.label))
      {
        const index = options.findIndex(x => x.label == option.label);
        if (index >= 0 && options[index]) options[index]!.isEnable = true;
        phaseId = (options[index]?.value as number) ?? 0;
        selectoption = { value: phaseId, label: option.label, isEnable: true };
      }
      else
      {
        phaseId = Math.max(...options.map(o => (typeof o.value === 'number' ? o.value : 0))) + 1;
        selectoption = { value: phaseId, label: option.label, isEnable: true };
        options.push(selectoption);
      }

      this.setState({ options }, () =>
      {
        this.handleChange(selectoption);
      });

      const newPhase = {
        PhaseId: phaseId,
        Title: option.label,
      };

      ConversationApi.CreateNewOptionPhase(this.props.api, newPhase);
    }
  }

  loadCatPhases(): void
  {
    ConversationApi.getPhases(this.props.api).then((response: any) =>
    {
      const options: SelectOption[] = [];
      for (let index = 0; index < response.length; index++)
      {
        const item: SelectOption = {
          value: response[index].PhaseId,
          label: response[index].Name,
          isEnable: response[index].isEnable,
        };
        options.push(item);
      }
      this.setState({ options: options });
    });
  }

  handlePhasesUpdated(e: Event): void
  {
    const event = e as CustomEvent<{ conversationId: string }>;
    const { conversationId } = event.detail;

    if (String(conversationId) === String(this.props.conversation.ConversationId))
    {
      // Reload phases from API directly (same as handlePhasesCreated)
      const { api, conversation } = this.props;
      ConversationApi.getConversationById(api, {
        conversationId: conversation.ConversationId,
        layersToHide: '',
        getAllReplies: false,
        isConversationDetailsView: false,
        totalReplies: 0,
      })
        .then((updatedConv: any) =>
        {
          this.setState({
            Phases: updatedConv.Phases,
          });
        })
        .catch((err: any) =>
        {
          console.error('Failed to reload phases:', err);
        });

      // Also call the callback if provided (for backwards compatibility)
      if (this.props.onReloadConversation)
      {
        this.props.onReloadConversation(this.props.conversation.ConversationId);
      }
      else
      {
        window.dispatchEvent(
          new CustomEvent('corgee:reload-conversation', {
            detail: { conversationId: this.props.conversation.ConversationId },
          }),
        );
      }
    }
  }

  handlePhasesCreated = (e: Event): void =>
  {
    const event = e as CustomEvent<{ conversationIds: string[]; conversation?: any }>;
    const { conversationIds, conversation: payloadConversation } = event.detail;

    if (conversationIds.includes(String(this.props.conversation.ConversationId)))
    {
      // This card triggered the phase creation and reloaded itself inline; skip the
      // redundant fetch to avoid a duplicate getConversationById request.
      if (this._skipNextSelfPhasesCreated)
      {
        this._skipNextSelfPhasesCreated = false;
        return;
      }

      // If the event carries the fresh conversation, use it instead of re-fetching.
      if (payloadConversation && payloadConversation.Phases)
      {
        this.setState({ Phases: payloadConversation.Phases });
        return;
      }

      const { api, conversation } = this.props;
      ConversationApi.getConversationById(api, {
        conversationId: conversation.ConversationId,
        layersToHide: '',
        getAllReplies: false,
        isConversationDetailsView: false,
        totalReplies: 0,
      })
        .then((updatedConv: any) =>
        {
          this.setState({
            Phases: updatedConv.Phases,
          });
        })
        .catch((err: any) =>
        {
          console.error('Failed to reload phases:', err);
        });
    }
  };

  handleOpen(): void
  {
    // Reflect open request in controlled state so the menu re-opens
    if (!this.state.phaseMenuOpen)
    {
      this.setState({ phaseMenuOpen: true });
    }

    setTimeout(() =>
    {
      const tabsH = this.refs.tabsH;
      const elementOuter = tabsH.getElementsByClassName('Select-menu-outer');
      const elementMenu = tabsH.getElementsByClassName('Select-menu');
      if (elementOuter.length > 0 && elementMenu.length > 0)
      {
        const positionMenuOuter = elementOuter[0].getBoundingClientRect();
        const selectMenu = elementMenu[0];
        selectMenu.setAttribute(
          'style',
          'left:' + (positionMenuOuter.left + 1) + 'px;' + 'top:' + positionMenuOuter.top + 'px',
        );
      }
    }, 0);
  }

  handleChange(option: SelectOption | null): void
  {
    if (option != null)
    {
      // Close the options menu as soon as a phase is selected.
      this.setState({ cmbSelect: option, phaseMenuOpen: false }, () =>
      {
        if (this.state.editingPhase === true)
        {
          this.addnewPhase();
        }
        else
        {
          this.renamePhase();
        }
      });
    }
    else if (this.state.cmbSelect.label.trim().length == 0)
    {
      const phases = this.state.Phases;
      if (this.state.editingPhase === true)
      {
        phases.splice(this.state.inputIndex, 1);
        this.setState({
          Phases: phases,
          inputValue: '',
          inputIndex: -1,
          editingPhase: false,
          phaseMenuOpen: false,
          validateInputValue: false,
        });
      }
      else
      {
        if (phases[this.state.inputIndex] != undefined)
        {
          (phases[this.state.inputIndex] as any).Edit = false;
        }
        this.setState({
          Phases: phases,
          inputValue: '',
          inputIndex: -1,
          phaseMenuOpen: false,
          validateInputValue: false,
        });
      }

      const tabsH = this.refs.tabsH;
      if (tabsH != undefined)
      {
        $(tabsH).closest('.sortableDashboard').off('scroll', this.scrollSortable);
        $(tabsH).closest('.corgee_com_card-body-info').off('scroll', this.scrollCardBody);
        $(tabsH).closest('.layer-custom-fields-container').off('scroll', this.scrollLayerContainer);
        $(tabsH).closest('.core2').off('scroll', this.scrollCore);

        const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected');
        if (tabs.length > 0)
        {
          tabs[0].setAttribute('style', '');
        }
      }
    }
    else
    {
      const blank: SelectOption = { label: '', value: '' };
      this.setState({
        cmbSelect: blank,
        validateInputValue: false,
        descriptionInputValue: "This field can't be empty",
      });
    }
  }

  onChangeTitle(nextPhases: ConversationPhase[], actualPhases: ConversationPhase[]): void
  {
    const itemsChanged: number[] = [];
    for (let index = 0; index < nextPhases.length; index++)
    {
      const nextPhase = nextPhases[index];
      if (!nextPhase) continue;
      for (let o = 0; o < actualPhases.length; o++)
      {
        const actualPhase = actualPhases[o];
        if (!actualPhase) continue;
        if (
          nextPhase.ConversationPhaseId == actualPhase.ConversationPhaseId &&
          nextPhase.Title != actualPhase.Title
        )
        {
          itemsChanged.push(index);
        }
      }
    }

    for (let k = 0; k < itemsChanged.length; k++)
    {
      const index = itemsChanged[k] ?? -1;
      if (index >= 0 && nextPhases[index]) nextPhases[index]!.Highlight = true;
    }

    this.setState({ Phases: nextPhases }, () =>
    {
      this.onShowArrows();
    });

    setTimeout(() =>
    {
      for (let k = 0; k < itemsChanged.length; k++)
      {
        const index = itemsChanged[k] ?? -1;
        if (index >= 0 && nextPhases[index]) nextPhases[index]!.Highlight = false;
      }
      this.setState({ Phases: nextPhases });
    }, 2000);
  }

  onShowArrows(): void
  {
    let childsTotalSize = 0;

    if (this.refs.tabsH != undefined)
    {
      for (const node of this.refs.tabsH.childNodes)
      {
        childsTotalSize = childsTotalSize + (node as HTMLElement).offsetWidth;
      }

      if (childsTotalSize > this.refs.tabsH.offsetWidth)
      {
        this.setState({ showArrows: true });
        return;
      }
    }

    this.setState({ showArrows: false });
  }

  scrollLft(): void
  {
    this.refs.tabsH.scrollBy({
      left: -this.state.distance,
      behavior: 'smooth',
    });
    this.setPositionMessage();
  }

  scrollRight(): void
  {
    this.refs.tabsH.scrollBy({
      left: this.state.distance,
      behavior: 'smooth',
    });
    this.setPositionMessage();
  }

  closeModalPhase(): void
  {
    this.setState({
      openModal: false,
      deletePhase: {
        title: '',
        deleteConversationId: 0,
        deleteConversationPhaseId: 0,
      },
    });
  }

  openModalPhase(
    _e: React.MouseEvent,
    Title: string,
    ConversationId: number,
    ConversationPhaseId: number,
  ): void
  {
    const tabsH = this.refs.tabsH;
    const IsTabsOpen = tabsH.getElementsByClassName('div-renamePhases');

    if (IsTabsOpen.length === 0)
    {
      this.setState({
        openModal: true,
        deletePhase: {
          title: Title,
          deleteConversationId: ConversationId,
          deleteConversationPhaseId: ConversationPhaseId,
        },
      });
    }
  }

  removePhase(): void
  {
    const actualIndex = this.state.tabIndex;
    const { conversation } = this.props;
    const phases = this.state.Phases;
    const item = {
      ConversationId: this.state.deletePhase.deleteConversationId,
      ConversationPhaseId: this.state.deletePhase.deleteConversationPhaseId,
      SocketConnectionId: this.props.SocketConnectionId,
    };

    const index = phases.findIndex(
      x =>
        x.ConversationId == this.state.deletePhase.deleteConversationId &&
        x.ConversationPhaseId == this.state.deletePhase.deleteConversationPhaseId,
    );

    phases.splice(index, 1);

    ConversationApi.deletePhase(this.props.api, item).then((response: any) =>
    {
      if (this.props.corgeeFunctions && this.props.corgeeFunctions.onRefreshPhaseSpreedShetDom)
      {
        (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom(conversation);
      }

      for (let f = 0; f < phases.length; f++)
      {
        const pf = phases[f];
        if (!pf) continue;
        for (let k = 0; k < response.length; k++)
        {
          const rk = response[k];
          if (!rk) continue;
          if (
            rk.ConversationId == pf.ConversationId &&
            rk.ConversationPhaseId == pf.ConversationPhaseId
          )
          {
            pf.Order = rk.Order;
          }
        }
      }

      phases.sort((a, b) => (a.Order > b.Order ? 1 : -1));

      // Emit event to notify other components (InfoWindow, Dashboard) that phases have been updated
      window.dispatchEvent(
        new CustomEvent('corgee:conversation-phases-updated', {
          detail: { conversationId: String(conversation.ConversationId) },
        }),
      );

      this.setState(
        {
          openModal: false,
          Phases: phases,
          deletePhase: {
            title: '',
            deleteConversationId: 0,
            deleteConversationPhaseId: 0,
          },
        },
        () =>
        {
          this.onShowArrows();

          if (phases.length == 0)
          {
            this.onAddHasPhase(false, []);
          }
          else
          {
            this.onAddHasPhase(true, phases);
          }

          const tabsH = this.refs.tabsH;
          if (tabsH != undefined)
          {
            const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected');
            if (tabs.length > 0)
            {
              if ($(tabs[0]).data('conversationphaseid') == 0)
              {
                return;
              }
            }
          }

          if (index + 1 == actualIndex)
          {
            this.setState({ tabIndex: 0 });
            this.reloadReplies(0);
          }
          else
          {
            setTimeout(() =>
            {
              this.setState({ tabIndex: actualIndex - 1 });
            }, 100);
          }
        },
      );
    });
  }

  renamePhase(): void
  {
    if (this.state.cmbSelect.label.trim().length == 0)
    {
      this.setPositionMessage();
      this.setState({
        validateInputValue: true,
        descriptionInputValue: "This field can't be empty",
      });
      return;
    }

    const phases = this.state.Phases;
    for (let index = 0; index < phases.length; index++)
    {
      if ((phases[index] as any).PhaseId == this.state.cmbSelect.value)
      {
        this.setPositionMessage();
        this.setState({
          validateInputValue: true,
          descriptionInputValue: 'This phase has already been added',
        });
        return;
      }
    }

    const phaseAtIndex = phases[this.state.inputIndex]!;
    const item = {
      ConversationId: phaseAtIndex.ConversationId,
      ConversationPhaseId: phaseAtIndex.ConversationPhaseId,
      SocketConnectionId: this.props.SocketConnectionId,
      Title: this.state.cmbSelect.label,
      PhaseId: this.state.cmbSelect.value,
    };

    (phaseAtIndex as any).Edit = false;
    phaseAtIndex.Title = this.state.cmbSelect.label;
    (phaseAtIndex as any).PhaseId = this.state.cmbSelect.value;
    (phaseAtIndex as any).isEnable = this.state.cmbSelect.isEnable;

    const index = this.state.inputIndex;
    this.setState({ Phases: phases, inputValue: '', inputIndex: -1, validateInputValue: false });

    const api = this.props.api;
    const tabsH = this.refs.tabsH;
    if (tabsH.getElementsByClassName('react-tabs__tab--selected').length > 0)
    {
      const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected')[0];
      tabs.setAttribute('style', '');
    }

    this.onShowArrows();

    ConversationApi.changeNameCard(api, item).then(() =>
    {
      this.reloadReplies(index + 1);
      if (
        this.props.corgeeFunctions &&
        (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom
      )
      {
        (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom(item);
      }
      ConversationApi.updatePhaseID(api, item);
    });
  }

  handleKeyUp(event: React.KeyboardEvent): void
  {
    if (
      event.type == 'click' ||
      event.type == 'mousedown' ||
      (event.type == 'keyup' && event.keyCode == 13) ||
      (event.type == 'keydown' && event.keyCode == 13)
    )
    {
      if (this.state.editingPhase === true)
      {
        this.addnewPhase();
      }
      else
      {
        this.renamePhase();
      }
    }

    if (
      (event.type == 'keyup' && event.keyCode == 27) ||
      (event.type == 'keydown' && event.keyCode == 27)
    )
    {
      event.stopPropagation();
      event.preventDefault();

      const phases = this.state.Phases;
      if (this.state.editingPhase === true)
      {
        phases.splice(this.state.inputIndex, 1);
        this.setState({
          Phases: phases,
          inputValue: '',
          inputIndex: -1,
          editingPhase: false,
          phaseMenuOpen: false,
          validateInputValue: false,
        });
      }
      else
      {
        (phases[this.state.inputIndex] as any).Edit = false;
        this.setState({
          Phases: phases,
          inputValue: '',
          inputIndex: -1,
          phaseMenuOpen: false,
          validateInputValue: false,
        });
      }
    }
  }

  notificationUpdateTab(ConversationPhaseId: number): void
  {
    if (this.state.phaseSelected.ConversationPhaseId == 0)
    {
      this.reloadReplies(0);
    }
    else if (ConversationPhaseId == this.state.phaseSelected.ConversationPhaseId)
    {
      const Phases = this.state.Phases;
      let index = Phases.findIndex(x => x.ConversationPhaseId == ConversationPhaseId);
      index = index + 1;
      this.reloadReplies(index);
    }
    else
    {
      if (ConversationPhaseId == 0)
      {
        this.setState({ highlightTabOriginal: true });
        setTimeout(() =>
        {
          this.setState({ highlightTabOriginal: false });
        }, 2000);
      }
      else
      {
        const Phases = this.state.Phases;
        const index = Phases.findIndex(x => x.ConversationPhaseId == ConversationPhaseId);
        if (index >= 0 && Phases[index]) Phases[index]!.Highlight = true;
        this.setState({ Phases: Phases });
        setTimeout(() =>
        {
          if (index >= 0 && Phases[index]) Phases[index]!.Highlight = false;
          this.setState({ Phases: Phases });
        }, 2000);
      }
    }
  }

  handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void
  {
    this.setState({ inputValue: event.target.value });
  }

  addtemporalphase(): void
  {
    if (this.state.isTabSaving || this.state.isCreatingPhase) return;
    this.loadCatPhases();
    setTimeout(() =>
    {
      const tabsH = this.refs.tabsH;
      const IsTabsOpen = tabsH.getElementsByClassName('div-renamePhases');

      if (IsTabsOpen.length === 0)
      {
        const Phases = cloneDeep(this.state.Phases);
        const temporalPhase: any = {
          Title: 'New phase',
          conversation: null,
          ConversationPhaseId: 0,
          ConfigValueId: 0,
          CoreTaskId: 0,
          TotalReplies: 0,
          TotalFavorites: 0,
          Edit: true,
          Disabled: true,
          ConversationId: 0,
          Order: 0,
        };

        Phases.push(temporalPhase);

        this.setState(
          {
            Phases: Phases,
            inputIndex: Phases.length - 1,
            inputValue: 'New Phase',
            editingPhase: true,
            phaseMenuOpen: true,
            cmbSelect: {
              value: '',
              label: '',
            },
          },
          () =>
          {
            setTimeout(() =>
            {
              const tabsH = this.refs.tabsH;
              this.scrollRight();
              this.positionScrollSorteable =
                $(tabsH).closest('.sortableDashboard').scrollTop() || 0;
              $(tabsH).closest('.sortableDashboard').on('scroll', this.scrollSortable);
              this.positionScrollCardBody =
                $(tabsH).closest('.corgee_com_card-body-info').scrollTop() || 0;
              $(tabsH).closest('.corgee_com_card-body-info').on('scroll', this.scrollCardBody);
              $(tabsH)
                .closest('.layer-custom-fields-container')
                .on('scroll', this.scrollLayerContainer);
              this.positionScrollCore = $(tabsH).closest('.core2').scrollTop() || 0;
              setTimeout(() =>
              {
                $(tabsH).closest('.core2').on('scroll', this.scrollCore);
              }, 0);
            }, 400);
          },
        );
      }
    });
  }

  setPositionMessage(): void
  {
    // May run from an async callback, when the ref can already be gone
    const tabsH = this.refs.tabsH;
    if (tabsH && tabsH.getElementsByClassName('div-renamePhases').length > 0)
    {
      const position = tabsH.getElementsByClassName('div-renamePhases')[0].getBoundingClientRect();
      this.setState({ top: position.top + 20, left: position.left });
    }
  }

  /**
   * ConfigValueId for a new phase. The conversation carries it only once the card has
   * phases, and the caches are empty outside MainLayout's routes (standalone
   * ConversationDetails window, Chat), so fall back to the API and cache the result.
   */
  async resolveConfigValueIdPhase(): Promise<number | null>
  {
    const { conversation, api } = this.props;

    if (conversation.ConfigValueIdPhase)
    {
      return conversation.ConfigValueIdPhase;
    }

    const cached = CorgeeStorageHelper.configValueIdPhases();
    if (cached)
    {
      return cached;
    }

    try
    {
      const peopleData: any = await MenuApi.getPeopleData(api);
      const fromApi = Number(peopleData?.ConfigValueIdPhases);

      if (!Number.isNaN(fromApi) && fromApi !== 0)
      {
        localStorage.setItem('C2_ConfigValueIdPhases', String(fromApi));
        return fromApi;
      }
    }
    catch (e)
    {
      console.error('Failed to resolve ConfigValueIdPhases:', e);
    }

    return null;
  }

  async addnewPhase(): Promise<void>
  {
    if (this.state.isTabSaving)
    {
      return;
    }

    if (this.state.cmbSelect.label.trim().length == 0)
    {
      this.setPositionMessage();
      this.setState({
        validateInputValue: true,
        descriptionInputValue: "This field can't be empty",
      });
      return;
    }

    const { conversation, api } = this.props;
    const phases = this.state.Phases;

    for (let index = 0; index < phases.length; index++)
    {
      if ((phases[index] as any).PhaseId == this.state.cmbSelect.value)
      {
        this.setPositionMessage();
        this.setState({
          validateInputValue: true,
          descriptionInputValue: 'This phase has already been added',
        });
        return;
      }
    }

    this.setState({ isTabSaving: true, isCreatingPhase: true });

    // Show the phase-creation loader for at least 5s (button stays hidden until then).
    if (this._phaseLoadingTimeout) clearTimeout(this._phaseLoadingTimeout);
    this._phaseLoadingTimeout = setTimeout(() =>
    {
      this.setState({ isCreatingPhase: false });
    }, 5000);

    // Resolved once here so createDefaultPhase/createPhase never post a null
    this._configValueIdPhase = await this.resolveConfigValueIdPhase();

    if (!this._configValueIdPhase)
    {
      this.reportPhaseFailure('resolveConfigValueIdPhase');
      return;
    }

    if (conversation && conversation.Phases != undefined && conversation.Phases.length == 0)
    {
      MenuApi.getCustomFieldsByItemId(
        api,
        conversation.CoreTaskId ?? 0,
        false,
        conversation.ProjectId,
      ).then((response: any) =>
      {
        let saveDefault = false;
        const copyCustoms: any[] = [];
        const customfields: any[] = [];

        for (let a = 0; a < response.length; a++)
        {
          for (let b = 0; b < response[a].CustomFields.length; b++)
          {
            if (
              response[a].CustomFields[b].Value != null &&
              response[a].CustomFields[b].Value != 'Not Set'
            )
            {
              if (
                response[a].CustomFields[b].Name == 'Estimated' ||
                response[a].CustomFields[b].Name == 'Remaining' ||
                response[a].CustomFields[b].Actual == 'Remaining' ||
                response[a].CustomFields[b].FieldTypeId === 7
              )
              {
                saveDefault = true;
              }

              const copyCustom = {
                CustomFieldId: response[a].CustomFields[b].CustomFieldId,
                Value: response[a].CustomFields[b].Value,
                CoreTaskID: 0,
                FieldTypeId: response[a].CustomFields[b].FieldTypeId,
                LayerId: response[a].LayerId,
                Name: response[a].CustomFields[b].Name,
                CreatedByID: response[a].CustomFields[b].CreatedByID,
              };
              copyCustoms.push(copyCustom);
            }
          }
        }

        if (saveDefault)
        {
          const ConfigValueIdPhase = this._configValueIdPhase;

          MenuApi.getEmptyCustomFieldsByConfigValueId(api, ConfigValueIdPhase ?? 0).then(
            (defaultCustoms: any) =>
            {
              for (let j = 0; j < copyCustoms.length; j++)
              {
                const layer = defaultCustoms.find((x: any) => x.LayerId == copyCustoms[j].LayerId);
                if (layer != undefined && layer.CustomFields.length > 0)
                {
                  for (let n = 0; n < layer.CustomFields.length; n++)
                  {
                    if (layer.CustomFields[n].Name === copyCustoms[j].Name)
                    {
                      const custom = {
                        CustomFieldId: layer.CustomFields[n].CustomFieldId,
                        Value: copyCustoms[j].Value,
                        CoreTaskID: 0,
                        FieldTypeId: copyCustoms[j].FieldTypeId,
                        CreatedByID: copyCustoms[j].CreatedByID,
                      };
                      customfields.push(custom);
                    }
                  }
                }
              }
              this.createDefaultPhase(customfields);
              setTimeout(() =>
              {
                this.createPhase(2, true);
                this.reloadReplies(this.state.tabIndex);
              }, 500);
            },
          )
            .catch((err: any) =>
            {
              this.setState({ isTabSaving: false, isCreatingPhase: false });
              console.error('Failed to load default custom fields for phase:', err);
            });
        }
        else
        {
          this.createPhase();
        }
      })
        .catch((err: any) =>
        {
          this.setState({ isTabSaving: false, isCreatingPhase: false });
          console.error('Failed to load custom fields for phase:', err);
        });
    }
    else
    {
      this.createPhase();
    }
  }

  // A phase inherits the card's layers and must never be created without one.
  // List payloads can carry ConversationLayers as null/empty, so fall back to the API.
  resolvePhaseLayers(origin: string, onResolved: (layers: any[]) => void): void
  {
    const { conversation, api } = this.props;
    const validLayers = (layers?: any[]) => (layers || []).filter((x: any) => x.LayerId != 0);

    const currentLayers = validLayers(conversation.ConversationLayers);
    if (currentLayers.length > 0)
    {
      onResolved(currentLayers);
      return;
    }

    ConversationApi.getConversationById(api, {
      conversationId: conversation.ConversationId,
      layersToHide: '',
      getAllReplies: false,
      isConversationDetailsView: false,
      totalReplies: 0,
    })
      .then((fetchedConv: any) =>
      {
        const fetchedLayers = validLayers(fetchedConv?.ConversationLayers);
        if (fetchedLayers.length > 0)
        {
          onResolved(fetchedLayers);
        }
        else
        {
          this.reportPhaseFailure(`${origin}: card has no layers`);
        }
      })
      .catch(() =>
      {
        this.reportPhaseFailure(origin);
      });
  }

  createPhase(realOrder?: number, reload?: boolean, layers?: any[]): void
  {
    if (!layers)
    {
      this.resolvePhaseLayers('createPhase', resolved => this.createPhase(realOrder, reload, resolved));
      return;
    }

    const { conversation, api, corgeeFunctions } = this.props;
    const phases = this.state.Phases;
    const newPhase: any = {
      Attachments: [],
      Body: '<span></span>',
      ConfigValueId: this._configValueIdPhase ?? conversation.ConfigValueIdPhase,
      ConversationGroups: [],
      ConversationId: 0,
      ConversationLayers: layers,
      CustomFields: [],
      Icon: '',
      IsAdmin: conversation.IsAdmin,
      IsFavorite: false,
      IsFromAssignmentReport: false,
      IsRedFlag: false,
      ProjectId: conversation.ProjectId,
      ProjectName: conversation.ProjectName,
      ShowDetails: true,
      SideMenuText: '',
      SocketConnectionId: this.props.SocketConnectionId || socketService.connectionId,
      Tags: [],
      Title: this.state.cmbSelect.label,
      UserExternal: false,
    };

    if (corgeeFunctions && corgeeFunctions.loadingBarStart)
    {
      corgeeFunctions.loadingBarStart();
    }

    ConversationApi.newcard(api, newPhase).then((response: any) =>
    {
      // callFetch resolves non-500/401 failures with the raw Response, so a 400
      // arrives here looking like success
      if (!response || !response.Conversation)
      {
        this.reportPhaseFailure('createPhase');
        return;
      }

      phases.splice(this.state.inputIndex, 1);
      const order = phases.length == 0 ? 0 : Math.max(...phases.map(o => o.Order));
      const item = {
        ConversationId: conversation.ConversationId,
        ConversationPhaseId: response.Conversation.ConversationId,
        Title: response.Conversation.Title,
        Order: realOrder != undefined ? realOrder : order + 1,
        SocketConnectionId: this.props.SocketConnectionId,
        PhaseId: this.state.cmbSelect.value,
      };

      ConversationApi.createNewPhase(api, item).then((phase: any) =>
      {
        if (
          this.props.corgeeFunctions &&
          (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom
        )
        {
          (this.props.corgeeFunctions as any).onRefreshPhaseShetDom(phase);
        }

        if (corgeeFunctions && corgeeFunctions.loadingBarComplete)
        {
          corgeeFunctions.loadingBarComplete();
        }

        const index = this.state.inputIndex;
        const blank: SelectOption = { label: '', value: '' };

        const convIdStr = String(conversation.ConversationId);

        if (reload === true)
        {
          // This card reloads the conversation ONCE and then broadcasts the fresh
          // payload. Other listeners (dashboard columns, other card instances, the
          // InfoWindow) update from that payload instead of each issuing their own
          // duplicate GetConversationById request.
          //
          // Guard so this card's own handlePhasesCreated listener does not re-fetch.
          // Skip the InfoWindow's details-view reload: whenever a payload is sent, the
          // InfoWindow card (a ConversationViewCard) updates itself from that payload.
          this._skipNextSelfPhasesCreated = true;
          const skipInfoWindowReload = true;

          // Keep isTabSaving/isCreatingPhase true so the "Add Phase" button stays
          // disabled until the reload below completes; otherwise the button becomes
          // clickable mid-flight and any opened inline input gets wiped by the reload.
          this.setState({
            editingPhase: false,
            inputValue: '',
            inputIndex: -1,
            validateInputValue: false,
            cmbSelect: blank,
          });

          ConversationApi.getConversationById(api, {
            conversationId: conversation.ConversationId,
            layersToHide: '',
            getAllReplies: false,
            isConversationDetailsView: false,
            totalReplies: 0,
          })
            .then((updatedConv: any) =>
            {
              // Keep saving flags until reloadReplies finishes (it clears them).
              this.setState(
                {
                  Phases: updatedConv.Phases,
                  tabIndex: 0,
                },
                () =>
                {
                  this.onAddHasPhase(true, updatedConv.Phases);
                  this.scrollRight();
                  this.reloadReplies(0);
                  const tabsH = this.refs.tabsH;
                  if (tabsH.getElementsByClassName('react-tabs__tab--selected').length > 0)
                  {
                    const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected')[0];
                    tabs.setAttribute('style', '');
                  }
                },
              );

              // Broadcast AFTER the reload, carrying the fresh conversation so other
              // listeners update from the payload (no extra GetConversationById calls).
              window.dispatchEvent(
                new CustomEvent('corgee:phases-created', {
                  detail: {
                    conversationIds: [convIdStr],
                    conversation: updatedConv,
                    skipInfoWindowReload,
                  },
                }),
              );
            })
            .catch((err: any) =>
            {
              this.setState({ isTabSaving: false, isCreatingPhase: false });
              console.error('Failed to reload conversation:', err);
              // Fallback: broadcast without payload so listeners reload themselves.
              window.dispatchEvent(
                new CustomEvent('corgee:phases-created', {
                  detail: { conversationIds: [convIdStr] },
                }),
              );
              if (this.props.onReloadConversation)
              {
                this.props.onReloadConversation(conversation.ConversationId);
              }
            });
        }
        else
        {
          const newPhaseItem: ConversationPhase = {
            ConversationPhaseId: phase.ConversationPhaseId,
            ConversationId: conversation.ConversationId,
            Title: phase.Title,
            Order: phase.Order,
            PhaseId:
              typeof this.state.cmbSelect.value === 'number'
                ? this.state.cmbSelect.value
                : undefined,
            CoreTaskId: phase.CoreTaskId,
          };
          phases.push(newPhaseItem);

          // The updated phases are already known locally (no fetch needed). Broadcast
          // them as the payload so every other surface (dashboard columns, other card
          // instances, the InfoWindow card) updates from it instead of each issuing its
          // own GetConversationById. Guard this card's own listener and skip the
          // InfoWindow details-view reload (the InfoWindow card updates from the payload).
          this._skipNextSelfPhasesCreated = true;
          window.dispatchEvent(
            new CustomEvent('corgee:phases-created', {
              detail: {
                conversationIds: [convIdStr],
                conversation: {
                  ConversationId: conversation.ConversationId,
                  Phases: phases,
                  HasPhases: true,
                },
                skipInfoWindowReload: true,
              },
            }),
          );

          // Keep saving flags until reloadReplies finishes (it clears them).
          this.setState(
            {
              Phases: phases,
              isTabSaving: true,
              isCreatingPhase: true,
              editingPhase: false,
              inputValue: '',
              inputIndex: -1,
              validateInputValue: false,
              cmbSelect: blank,
              tabIndex: index + 1,
            },
            () =>
            {
              this.props.onTabChange?.(index + 1);
              setTimeout(() =>
              {
                this.onAddHasPhase(true, phases);
                this.scrollRight();
                this.reloadReplies(index + 1);
                const tabsH = this.refs.tabsH;
                if (tabsH.getElementsByClassName('react-tabs__tab--selected').length > 0)
                {
                  const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected')[0];
                  tabs.setAttribute('style', '');
                }
              }, 500);
            },
          );
        }
      });
    })
      .catch((err: any) =>
      {
        // Re-enable the "Add Phase" button if creation fails so it is not stuck disabled
        this.setState({ isTabSaving: false, isCreatingPhase: false });
        console.error('Failed to create phase:', err);
      });
  }

  /**
   * Releases the UI after a failed phase creation; otherwise the Add Phase button
   * stays disabled and the loader never stops.
   */
  reportPhaseFailure(origin: string): void
  {
    console.error(`Failed to create phase (${origin}).`);

    if (this._phaseLoadingTimeout) clearTimeout(this._phaseLoadingTimeout);

    const { corgeeFunctions } = this.props;
    if (corgeeFunctions && corgeeFunctions.loadingBarComplete)
    {
      corgeeFunctions.loadingBarComplete();
    }

    this.setPositionMessage();
    this.setState({
      isTabSaving: false,
      isCreatingPhase: false,
      validateInputValue: true,
      descriptionInputValue: 'The phase could not be created. Please try again.',
    });
  }

  createDefaultPhase(customfields: any[], layers?: any[]): void
  {
    if (!layers)
    {
      this.resolvePhaseLayers('createDefaultPhase', resolved => this.createDefaultPhase(customfields, resolved));
      return;
    }

    const { conversation, api, corgeeFunctions } = this.props;
    const phases = this.state.Phases;

    const newPhase: any = {
      Attachments: [],
      Body: '<span></span>',
      ConfigValueId: this._configValueIdPhase ?? conversation.ConfigValueIdPhase,
      ConversationGroups: [],
      ConversationId: 0,
      ConversationLayers: layers,
      CustomFields: customfields,
      Icon: '',
      IsAdmin: conversation.IsAdmin,
      IsFavorite: false,
      IsFromAssignmentReport: false,
      IsRedFlag: false,
      ProjectId: conversation.ProjectId,
      ProjectName: conversation.ProjectName,
      ShowDetails: true,
      SideMenuText: '',
      SocketConnectionId: this.props.SocketConnectionId || socketService.connectionId,
      Tags: [],
      Title: 'Default',
      UserExternal: false,
    };

    if (corgeeFunctions && corgeeFunctions.loadingBarStart)
    {
      corgeeFunctions.loadingBarStart();
    }

    ConversationApi.newcard(api, newPhase).then((response: any) =>
    {
      if (!response || !response.Conversation)
      {
        this.reportPhaseFailure('createDefaultPhase');
        return;
      }

      phases.splice(this.state.inputIndex, 1);
      const order = phases.length == 0 ? 0 : Math.max(...phases.map(o => o.Order));
      const item = {
        ConversationId: conversation.ConversationId,
        ConversationPhaseId: response.Conversation.ConversationId,
        Title: response.Conversation.Title,
        Order: order + 1,
        SocketConnectionId: this.props.SocketConnectionId,
        PhaseId: -1,
      };

      ConversationApi.createNewPhase(api, item).then((phase: any) =>
      {
        if (
          this.props.corgeeFunctions &&
          (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom
        )
        {
          (this.props.corgeeFunctions as any).onRefreshPhaseSpreedShetDom(phase);
        }
        if (corgeeFunctions && corgeeFunctions.loadingBarComplete)
        {
          corgeeFunctions.loadingBarComplete();
        }
      });
    });
  }

  onRepliesWindowState = (fullyShown: boolean): void =>
  {
    if (this.state.repliesFullyShown !== fullyShown)
    {
      this.setState({ repliesFullyShown: fullyShown });
    }
  };

  reloadReplies(index: number): void
  {
    this.onShowArrows();
    const layersToHide = CorgeeStorageHelper.getLayersToHide();
    const indexFix = index - 1;
    const phases = this.state.Phases;

    const requestParams: any = {
      ConversationId: this.props.conversation.ConversationId,
      LayersToHide: layersToHide !== null ? layersToHide : 0,
      RecycleBinDashboard: !this.props.conversation.IsEnable,
      pageNumber: 1,
      pageSize: 10,
      isConversationDetailsView: false,
      ConversationPhaseId: index == 0 ? 0 : (phases[indexFix]?.ConversationPhaseId ?? 0),
      replyTab: this.state.selectedReplyTab ? this.state.selectedReplyTab : 'all',
    };

    this.setState({
      showReplies: false,
    });

    // Reply tabs (comments/worklog/activity) return a filtered list. This window's own
    // counter always follows its tab (rendered from state), but the conversation-wide
    // props shared with other windows of the same card are only rewritten by 'all'.
    const isAllRepliesTab = (this.state.selectedReplyTab || 'all') === 'all';

    // The counter keeps its current value while loading (no flash to 0).
    this.onResetRepliesList([]);

    ConversationApi.getMoreReplies(this.props.api, requestParams)
      .then((response: Reply[]) =>
      {
        for (let f = 0; f < response.length; f++)
        {
          (response[f] as any).reloadReply = true;
        }

        const totalReplies = response.filter(
          x => x.OriginalConversationId == 0 || x.OriginalConversationId == undefined,
        ).length;
        const totalFavorites =
          response.filter(
            x => x.OriginalConversationId == 0 || x.OriginalConversationId == undefined,
          ).length > 0
            ? response.filter(x => x.IsFavorite == true).length
            : 0;

        let last3Replies = response;
        if (response.length > 3)
        {
          last3Replies = response.slice(response.length - 3, response.length);
        }

        this.setState({
          replies: last3Replies,
          phaseSelected: {
            ConversationPhaseId: index == 0 ? 0 : (phases[indexFix]?.ConversationPhaseId ?? 0),
            Title:
              index == 0 ? 'All Phases' : phases[indexFix]?.Title || phases[indexFix]?.Name || '',
            CoreTaskId:
              index == 0
                ? this.props.conversation.CoreTaskId
                : (phases[indexFix]?.CoreTaskId ?? null),
          },
          totalFavorites,
          totalReplies,
          showDetails: true,
          showReplies: true,
          isTabSaving: false,
          // <= 3 rows: everything already visible (an empty phase included)
          repliesFullyShown: response.length <= 3,
        });

        this.onResetRepliesList(last3Replies);

        if (this.props.onUpdateConversation && isAllRepliesTab)
        {
          const newAttrs: Partial<ConversationDetail> = {
            TotalFavorites: totalFavorites,
            TotalReplies: totalReplies,
          };
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            newAttrs,
            false,
            'replies',
          );
        }
      })
      .catch(error =>
      {
        // Reset saving flags so the Add Phase button isn't stuck disabled.
        this.setState({ isTabSaving: false, isCreatingPhase: false });
        console.error(error);
      });
  }

  // Wraps onAddLayerToProject for the reply context: skips the project-conversation sync
  // (GetConversationById + GetGroupsByProject + updateconversation) which is only needed
  // when adding a layer from the card header, not from a reply.
  addLayerToProjectFromReply = (e: any, layer: any, fns?: any) =>
  {
    this.props.onAddLayerToProject?.(e, layer, { ...fns, skipProjectSync: true });
  };

  handleAddReply(conversationId: number, reply: any, isUpdate: boolean, isNew: boolean): void
  {
    const conversation = this.props.conversation;

    if (isNew && !isUpdate)
    {
      // Temporal reply: make a snapshot copy so later mutations to the original object
      // (by ConversationCardAddReply) don't affect the rendered temporal reply.
      const temporalCopy = { ...reply };
      const currentReplies = conversation.Replies ? [...conversation.Replies] : [];
      currentReplies.push(temporalCopy);

      // Save timestamp so we can ensure the temporal animation is visible for a minimum time
      (this as any)._temporalReplyAddedAt = Date.now();

      if (this.props.onUpdateConversation)
      {
        const newAttrs: any = {
          Replies: currentReplies,
          TotalReplies: (conversation.TotalReplies || 0) + 1,
          NoUpdateTotalReplies: true,
        };
        this.props.onUpdateConversation(conversationId, newAttrs);
      }

      this.setState({
        totalReplies: (this.state.totalReplies || 0) + 1,
        showReplies: true,
      });
    }
    else if (isUpdate && !isNew)
    {
      // Mark synchronously, before doReplace (which may run up to 800ms later via the
      // setTimeout below): ConversationCardAddReply calls onAddReply(isUpdate=true) and then
      // emits the 'reply-added' bus event on the very next line, both synchronously, well
      // before doReplace's delayed update ever reaches props. The bus listener needs this
      // mark available immediately to recognize its own echo.
      if (reply.ReplyId > 0)
      {
        this.recentlyConfirmedReplyIds.add(reply.ReplyId);
        setTimeout(() => this.recentlyConfirmedReplyIds.delete(reply.ReplyId), 5000);
      }

      const doReplace = () =>
      {
        const conv = this.props.conversation;
        const currentReplies = conv.Replies ? [...conv.Replies] : [];
        const temporalIndex = currentReplies.findIndex((r: any) => r.TemporalReply === true);

        if (temporalIndex >= 0)
        {
          currentReplies[temporalIndex] = { ...reply };
        }
        else
        {
          // Check if reply with this ID already exists to avoid duplication
          const existingIndex = reply.ReplyId > 0
            ? currentReplies.findIndex((r: any) => r.ReplyId === reply.ReplyId)
            : -1;

          if (existingIndex >= 0)
          {
            // Reply already exists, update it instead of adding
            currentReplies[existingIndex] = { ...reply };
          }
          else
          {
            // Reply doesn't exist, add it
            currentReplies.push({ ...reply });
          }
        }

        if (this.props.onUpdateConversation)
        {
          const newAttrs: any = {
            Replies: currentReplies,
            NoUpdateTotalReplies: true,
          };
          this.props.onUpdateConversation(conversationId, newAttrs);
        }

        // Notify parent only once (isUpdate=true), after server confirms the reply.
        // Calling it on isNew would cause the parent to also increment TotalReplies,
        // doubling the count since ConversationViewCard already handles it via onUpdateConversation.
        if (this.props.onAddReply)
        {
          this.props.onAddReply(conversationId, reply);
        }
      };

      // Ensure the temporal "..." animation is visible for at least 800ms
      const addedAt = (this as any)._temporalReplyAddedAt || 0;
      const elapsed = Date.now() - addedAt;
      const minDisplayTime = 800;

      if (elapsed < minDisplayTime)
      {
        setTimeout(doReplace, minDisplayTime - elapsed);
      }
      else
      {
        doReplace();
      }
    }
  }

  // ==================== Gallery & Images ====================

  onCloseGallery(): void
  {
    this.setState({
      embeddedImages: [],
      openGallery: false,
      selectedImageId: 0,
    });
  }

  handleEmbeddedImagesClick(e: React.MouseEvent): void
  {
    if (handleLinkedCardClick(e, id =>
      (this.props.corgeeFunctions as any)?.openInfoWindow?.(id),
    )) return;

    if (handleOriginalConversationLinkClick(e, id =>
      this.props.corgeeFunctions?.openInfoWindow?.({ ConversationId: id } as any),
    )) return;

    const target = e.target as HTMLElement;
    const imgElement = target.tagName === 'IMG' ? (target as HTMLImageElement) : null;

    if (imgElement)
    {
      e.preventDefault();
      const ev = 'EmbeddedImages/';
      ConversationApi.getEmbeddedImages(this.props.api, {
        id: this.props.conversation.ConversationId,
        IsReply: false,
      }).then((response: EmbeddedImage[]) =>
      {
        const imgSrc = imgElement.src;
        let selectedImageId = 0;

        for (let i = 0; i < response.length; i++)
        {
          const element = response[i];
          if (!element) continue;
          // Match by Thumbnail.FileName (legacy) or by full FileUrl/Thumbnail URL
          const thumbFileName = (element as any).Thumbnail?.FileName || '';
          const fileName =
            imgSrc.indexOf(ev) >= 0
              ? imgSrc
                .substr(imgSrc.indexOf(ev) + ev.length)
                .replace(imgSrc.substr(imgSrc.lastIndexOf('.')), '')
              : '';
          if (
            (thumbFileName && thumbFileName === fileName) ||
            element.FileUrl === imgSrc ||
            (element as any).Thumbnail?.FileUrl === imgSrc
          )
          {
            selectedImageId = element.EmbeddedImageId;
            break;
          }
        }

        this.setState({
          openGallery: true,
          embeddedImages: response,
          selectedImageId: selectedImageId,
        });
      });
    }
  }

  // ==================== Replies Management ====================

  onUpdateTotalFavorites(
    replyId: number,
    isFavorite: boolean,
    totalFavoritesToAdd: number,
    favoriteCount: number,
  ): void
  {
    const newTotalFavorites = this.state.totalFavorites + totalFavoritesToAdd;

    this.setState({ totalFavorites: newTotalFavorites });

    if (this.props.onUpdateConversation)
    {
      const newAttrs: Partial<ConversationDetail> = {
        TotalFavorites: newTotalFavorites,
      };
      this.props.onUpdateConversation(
        this.props.conversation.ConversationId,
        newAttrs,
        false,
        'replies',
      );
    }

    if (this.props.onUpdateReply)
    {
      const newAttrs: Partial<Reply> = {
        IsFavorite: isFavorite,
        FavoriteCount: favoriteCount,
        FavoriteDisable: false as any,
      };
      this.props.onUpdateReply(
        this.props.conversation.ConversationId,
        replyId,
        newAttrs,
        newTotalFavorites,
      );
    }
  }

  resetSeeMoreReplies(): void
  {
    this.setState({ seeMoreRepliesGrayBar: false });
  }

  setLoadingMoreReplies(loading: boolean): void
  {
    this.setState({ isLoadingMoreReplies: loading });
  }

  countReplies(): number
  {
    const { conversation } = this.props;
    const replies = conversation.Replies.filter(
      x => x.OriginalConversationId == 0 || x.OriginalConversationId == undefined,
    );
    const uniqueIds: number[] = [];

    for (let index = 0; index < replies.length; index++)
    {
      const reply = replies[index];
      if (reply && uniqueIds.indexOf(reply.ReplyId) < 0)
      {
        uniqueIds.push(reply.ReplyId);
      }
    }

    return uniqueIds.length;
  }

  onSeeMoreReplies(seeMoreRepliesParam: boolean): void
  {
    const { pageNumber, isFromAddReply } = this.state;

    let newPageNumber = 0;
    if (isFromAddReply)
    {
      this.setState({ isFromAddReply: false }, () =>
      {
        const convPhaseId = this.onGetPhaseSelected();
        const pTotalReplies =
          this.props.conversation.TotalReplies +
          (convPhaseId == 0 ? this.props.conversation.TotalTasks : 0);
        if (
          !seeMoreRepliesParam &&
          pTotalReplies ===
            this.countReplies() + (convPhaseId == 0 ? this.props.conversation.TotalTasks : 0)
        )
        {
          newPageNumber = this.state.pageNumber == 1 ? 0 : 1;
        }
        else
        {
          newPageNumber = seeMoreRepliesParam ? this.state.pageNumber + 1 : 0;
        }
        this.setState({ seeMoreReplies: seeMoreRepliesParam, pageNumber: newPageNumber });
      });
    }
    else
    {
      newPageNumber = seeMoreRepliesParam ? pageNumber + 1 : pageNumber == 1 ? 0 : 1;
      this.setState({ seeMoreReplies: seeMoreRepliesParam, pageNumber: newPageNumber });
    }
  }

  // ==================== Highlighting ====================

  highlightSeeMore(): void
  {
    this.setState({ highlightSM: true });
    setTimeout(() =>
    {
      this.setState({ highlightSM: false });
    }, 2000);
  }

  highlightBody(): void
  {
    this.setState({ highlight: true });
    setTimeout(() =>
    {
      this.setState({ highlight: false });
    }, 3000);
  }

  highlightCard(): void
  {
    clearTimeout(this.highlightCardTimeout);
    if (this.highlightCardRafId !== undefined)
    {
      cancelAnimationFrame(this.highlightCardRafId);
      this.highlightCardRafId = undefined;
    }

    this.setState({ highlightCard: false }, () =>
    {
      this.highlightCardRafId = requestAnimationFrame(() =>
      {
        this.highlightCardRafId = undefined;
        this.setState({ highlightCard: true });
        this.highlightCardTimeout = setTimeout(() =>
        {
          this.setState({ highlightCard: false });
        }, 3000);
      });
    });
  }

  highlightHeader(): void
  {
    this.setState({ highlightHeader: true });
    setTimeout(() =>
    {
      this.setState({ highlightHeader: false });
    }, 3000);
  }

  highlightAttachments(): void
  {
    this.setState({ highlightAttachment: true });
    setTimeout(() =>
    {
      this.setState({ highlightAttachment: false });
    }, 3000);
  }

  // ==================== Lifecycle Methods ====================

  componentDidUpdate(prevProps: ConversationViewCardProps, prevState: ConversationViewCardState): void
  {
    // Re-bind if React swapped the card body element on re-render
    this.attachCardBodyScrollLeftPin();

    if (prevState.showSeeMoreLink === true && this.state.showSeeMoreLink === false && this.state.displayFullContent === true)
    {
      this.setState({ displayFullContent: false });
    }

    // Sync SocketConnectionId when it changes
    if (this.props.SocketConnectionId !== prevProps.SocketConnectionId)
    {
      const requestConfig = this.state.requestConfig;
      requestConfig.SocketConnectionId = this.props.SocketConnectionId;
      this.setState({ requestConfig });
    }

    if (this.props.conversation && prevProps.conversation)
    {
      // Sync totalFavorites and totalReplies when they change (unless blocked)
      const noUpdate = (this.props.conversation as any).NoUpdateTotalReplies;
      if (noUpdate === false || noUpdate == null || noUpdate === undefined)
      {
        if (
          this.props.conversation.TotalFavorites !== prevProps.conversation.TotalFavorites &&
          this.props.conversation.TotalFavorites !== this.state.totalFavorites
        )
        {
          this.setState({ totalFavorites: this.props.conversation.TotalFavorites });
        }
        // conversation.TotalReplies is the conversation-wide total across every phase.
        // While a phase tab is selected, state.totalReplies holds the phase-scoped
        // count set from the filtered fetch, so syncing from props here would
        // overwrite it with the larger global figure after a reply is added.
        const isPhaseFiltered = (this.state.phaseSelected?.ConversationPhaseId ?? 0) > 0;

        if (
          !isPhaseFiltered &&
          this.props.conversation.TotalReplies !== prevProps.conversation.TotalReplies &&
          this.props.conversation.TotalReplies !== this.state.totalReplies
        )
        {
          this.setState({ totalReplies: this.props.conversation.TotalReplies });
        }
      }

      // Handle NoUpdateTotalReplies becoming true
      if (
        (this.props.conversation as any).NoUpdateTotalReplies === true &&
        (prevProps.conversation as any).NoUpdateTotalReplies !== true
      )
      {
        if (this.props.onUpdateConversation)
        {
          const newAttrs: any = {
            TotalFavorites: this.state.totalFavorites,
            TotalReplies: this.state.totalReplies,
            NoUpdateTotalReplies: false,
          };
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            newAttrs,
            false,
            'replies',
          );
        }
      }

      // Sync Phases when they change — skip while user is actively adding/editing a phase
      // to prevent an in-flight reload from wiping out the temporary editing entry.
      if (!this.deepEqual(this.props.conversation.Phases, prevProps.conversation.Phases) && !this.state.editingPhase)
      {
        if (this.props.conversation.Phases.length === this.state.Phases.length)
        {
          this.onChangeTitle(this.props.conversation.Phases, this.state.Phases);
        }
        else if (this.props.conversation.Phases.length > 0)
        {
          const index = this.props.conversation.Phases.length - 1;
          const phases = this.props.conversation.Phases;
          if (phases[index]) phases[index]!.Highlight = true;

          this.setState({ Phases: phases, highlightTab: true }, () =>
          {
            this.onShowArrows();
          });

          setTimeout(() =>
          {
            if (phases[index]) phases[index]!.Highlight = false;
            this.setState({ Phases: phases, highlightTab: false });
          }, 2000);
        }
        else if (this.props.conversation.Phases.length === 0)
        {
          this.setState({ Phases: [] }, () =>
          {
            this.onShowArrows();
          });
        }
      }

      // Sync body and trigger highlight animation
      if (
        this.props.conversation.Body !== prevProps.conversation.Body &&
        this.props.conversation.Body !== this.state.body
      )
      {
        this.setState({ body: this.props.conversation.Body });
        this.onCancelEditBody();
        if (!(this.props.conversation as any).reactSelfUpdate)
        {
          this.highlightBody();
        }
        else
        {
          this.setState({ reactSelfUpdate: false });
        }
      }

      // Trigger header highlight on Finished change
      if (this.props.conversation.Finished !== prevProps.conversation.Finished)
      {
        if (!(this.props.conversation as any).reactSelfUpdate)
        {
          this.highlightHeader();
        }
      }

      // Trigger header highlight on Title change
      if (this.props.conversation.Title !== prevProps.conversation.Title)
      {
        if (!(this.props.conversation as any).reactSelfUpdate)
        {
          this.highlightHeader();
        }
      }

      // Sync isImportant when prop changes externally (e.g. socket refresh)
      if (
        this.props.conversation.IsImportant !== prevProps.conversation.IsImportant &&
        this.props.conversation.IsImportant !== this.state.isImportant
      )
      {
        this.setState({ isImportant: this.props.conversation.IsImportant });
      }

      // Sync reactSelfUpdate state — only update when value actually changes to avoid infinite loops
      if ((this.props.conversation as any).reactSelfUpdate !== undefined)
      {
        if (this.state.reactSelfUpdate !== false)
        {
          this.setState({ reactSelfUpdate: false });
        }
      }
      else
      {
        // Trigger attachments highlight when attachments count changes
        if (
          (this.props.conversation.Attachments?.length ?? 0) !==
            (prevProps.conversation.Attachments?.length ?? 0) &&
          !(this.props.conversation as any).reactSelfUpdate
        )
        {
          this.highlightAttachments();
          this.setState({ reactSelfUpdate: false });
        }
        if ((this.props.conversation as any).reactSelfUpdate)
        {
          this.setState({ reactSelfUpdate: (this.props.conversation as any).reactSelfUpdate });
        }
      }

      // Handle reply highlighting
      if (
        this.props.replyToHighlight != null &&
        this.props.replyToHighlight !== prevProps.replyToHighlight
      )
      {
        const replyIds: number[] = [];
        this.props.conversation.Replies.map((x: Reply) =>
        {
          replyIds.push(x.ReplyId);
        });
        const indexOfExistingReplies = replyIds.indexOf(this.props.replyToHighlight);
        const indexOfReply = this.state.repliesPendingToHighlight.indexOf(
          this.props.replyToHighlight,
        );
        if (indexOfReply === -1 && indexOfExistingReplies === -1)
        {
          const pending = Object.assign([], this.state.repliesPendingToHighlight);
          pending.push(this.props.replyToHighlight);
          this.setState({
            repliesPendingToHighlight: pending,
          });
        }
      }

      // Handle shouldUpdateHideReplies
      if (this.props.shouldUpdateHideReplies && !prevProps.shouldUpdateHideReplies)
      {
        const replies = Object.assign([], this.state.repliesPendingToHighlight);
        let shouldHighlight = false;
        const replyIds: number[] = [];
        this.props.conversation.Replies.map((x: Reply) =>
        {
          replyIds.push(x.ReplyId);
        });
        for (let j = 0; j < replies.length; j++)
        {
          const indexOnReplies = replyIds.indexOf(replies[j] ?? -1);
          if (indexOnReplies === -1)
          {
            shouldHighlight = true;
            continue;
          }
        }
        if (shouldHighlight)
        {
          this.highlightSeeMore();
        }

        if (this.props.restoreHideRepliesHighlight)
        {
          this.props.restoreHideRepliesHighlight(true);
        }
      }

      // Sync firstLayerColor when ConversationLayers change
      if (
        this.props.conversation.ConversationLayers &&
        this.props.conversation.ConversationLayers.length > 0
      )
      {
        let layers = this.props.conversation.ConversationLayers;
        layers = layers.sort((a, b) => (a.PriorityInConfigValue || 0) - (b.PriorityInConfigValue || 0));
        layers = layers.filter(x => x.LayerId !== 0);
        const firstLayerColor = layers[0]?.LayerColor
          ? layers[0].LayerColor
          : (layers[0] as any)?.Color;
        if (firstLayerColor !== this.state.firstLayerColor)
        {
          this.setState({
            firstLayerColor: firstLayerColor,
          });
        }
      }

      // Sync HasPhases
      if (
        this.props.conversation.HasPhases !== prevProps.conversation.HasPhases &&
        this.props.conversation.HasPhases !== this.state.HasPhases
      )
      {
        this.setState({ HasPhases: this.props.conversation.HasPhases });
      }

      // Sync Phases when it changes (conversation loads async on linked cards)
      if (this.props.conversation.Phases !== prevProps.conversation.Phases)
      {
        this.setState({ Phases: this.props.conversation.Phases || [] });
      }

      // Sync enablePhases: CoreTaskId arrives async, so the constructor value can be stale
      const nextEnablePhases = this.props.conversation.CoreTaskId != null;
      if (
        this.props.conversation.CoreTaskId !== prevProps.conversation.CoreTaskId &&
        nextEnablePhases !== this.state.enablePhases
      )
      {
        this.setState({ enablePhases: nextEnablePhases });
      }
    }

    // Sync showDetails when forceShowDetails changes
    if (this.props.forceShowDetails !== prevProps.forceShowDetails)
    {
      this.setState({ showDetails: this.props.forceShowDetails === true });
    }

    // Handle UpdateTab notifications
    if (
      this.props.UpdateTab &&
      this.props.UpdateTab.Enable === true &&
      (!prevProps.UpdateTab || prevProps.UpdateTab.Enable !== true)
    )
    {
      if (this.props.onStopUpdateTab)
      {
        this.props.onStopUpdateTab();
      }
      this.notificationUpdateTab(this.props.UpdateTab.ConversationPhaseId);
    }

    // Re-measure height and rebind image events only when body content changes
    // (initial measurement is handled in componentDidMount)
    if (this.props.conversation.Body !== prevProps.conversation.Body)
    {
      setTimeout(() =>
      {
        const element = 'cardElementDescription_' + this.props.conversation.ConversationId;
        const cardContainer =
          this.refs[element] != undefined &&
          (this.refs[element] as any).querySelector('.card-content-container-height-listener') !=
            undefined
            ? (this.refs[element] as any).querySelector('.card-content-container-height-listener')
            : null;

        if (this.refs[element] && cardContainer)
        {
          const finalHeight = cardContainer.scrollHeight;

          if (finalHeight > 137 && this.hasVisibleContent(cardContainer))
          {
            this.setState({ showSeeMoreLink: true });
          }
          else if (this.state.showSeeMoreLink !== false)
          {
            this.setState({ showSeeMoreLink: false });
          }

          this.countImages(this.refs[element], finalHeight);
        }
        this.openLinkOnNewWindow();
      }, 600);
    }
  }

  removePendingHighlight(replyId: number): void
  {
    const toRemove = this.state.repliesPendingToHighlight.indexOf(replyId);
    if (toRemove != -1)
    {
      const pending = Object.assign([], this.state.repliesPendingToHighlight);
      pending.splice(toRemove, 1);
      this.setState(
        {
          repliesPendingToHighlight: pending,
        },
        () =>
        {
          if (this.props.removeUpdateButton && typeof this.props.removeUpdateButton == 'function')
          {
            this.props.removeUpdateButton();
          }
        },
      );
    }
  }

  onAddHasPhase(hasphase: boolean, phases: ConversationPhase[]): void
  {
    if (this.props.onUpdateConversation)
    {
      const newAttrs: Partial<ConversationDetail> = {
        HasPhases: hasphase,
        Phases: phases,
      };
      this.props.onUpdateConversation(this.props.conversation.ConversationId, newAttrs, false);
    }
  }

  onAddRepost(reply: Reply): void
  {
    this.setState({ openRepost: true, itemRepost: reply });
  }

  setShowRepost(): void
  {
    this.setState({ openRepost: false });
  }

  onUpdateReply(reply: Reply, replyId: number): void
  {
    if (this.props.onUpdateReply)
    {
      const newAttrs: Partial<Reply> = {
        Body: reply.Body,
        Attachments: reply.Attachments,
        UpdatedBy: reply.UpdatedBy,
        UpdatedAt: reply.UpdatedAt,
      };

      // Layers can change from the Edit Reply modal
      if (reply.ReplyLayers)
      {
        newAttrs.ReplyLayers = reply.ReplyLayers;
      }
      this.props.onUpdateReply(this.props.conversation.ConversationId, replyId, newAttrs);
    }
  }

  setShowDetails(): void
  {
    const { showDetails } = this.state;

    let text: string;
    if (showDetails)
    {
      text = 'Show Details';
    }
    else
    {
      text = 'Hide Details';
    }

    this.setState({ showDetails: !showDetails, textDetailsSection: text });
  }

  setIsFromAddReply(status: boolean): void
  {
    this.setState({ isFromAddReply: status });
  }

  onResetRepliesList(repliesList: Reply[], updateMessageMoreReplies?: boolean): void
  {
    if (this.props.onUpdateConversation)
    {
      const newAttrs: any = {
        Replies: repliesList,
      };
      this.props.onUpdateConversation(this.props.conversation.ConversationId, newAttrs, false);
    }

    if (updateMessageMoreReplies)
    {
      this.setState({ updateMessageMoreReplies: true });
      setTimeout(() =>
      {
        this.setState({ updateMessageMoreReplies: false });
      }, 0);
    }
  }

  // ==================== Body Editing ====================

  openLinkOnNewWindow(): void
  {
    const indexOfLink = this.state.linkInNewWindow.indexOf(this.props.conversation.ConversationId);
    if (indexOfLink == -1)
    {
      const links = Object.assign([], this.state.linkInNewWindow);
      links.push(this.props.conversation.ConversationId);
      this.setState({ linkInNewWindow: links });

      void this.props.config?.weburl;
      const element = 'cardElementDescription_' + this.props.conversation.ConversationId;

      // Store project in localStorage
      const objBeforeSearch = JSON.parse(
        localStorage.getItem('C2_BeforeSearchObjectProject') || 'null',
      );
      const projectIdVal = this.props.conversation.ProjectId;
      const UpdateAtVal = this.props.conversation.UpdatedAt;
      const ProjectNameVal = this.props.conversation.ProjectName;

      if (objBeforeSearch == undefined || objBeforeSearch == null || objBeforeSearch.length == 0)
      {
        const strObjBeforeSearch = JSON.stringify([
          {
            ID: projectIdVal,
            Icon: 'icons-menu-assignment',
            ImageName: null,
            IsDashboardsFilter: false,
            LastUpdatedAt: UpdateAtVal,
            Name: ProjectNameVal,
            Order: 1,
            ProjectId: 0,
            ProjectName: null,
            Type: ProjectNameVal,
            TypeId: 9,
            isOnLine: false,
          },
        ]);
        localStorage.setItem('C2_BeforeSearchObjectProject', strObjBeforeSearch);
      }
      else
      {
        if (objBeforeSearch.length == 10)
        {
          objBeforeSearch.splice(0, 1);
        }
        let id = 0;

        $.each(objBeforeSearch, (_i: number, obj: any) =>
        {
          if (projectIdVal == obj.ID)
          {
            id = obj.ID;
          }
        });

        const objBeforeSearchNew = $.grep(objBeforeSearch, (e: any) =>
        {
          return e.ID != id;
        });

        const ObjBeforeSearch = {
          ID: projectIdVal,
          Icon: 'icons-menu-assignment',
          ImageName: null,
          IsDashboardsFilter: false,
          LastUpdatedAt: UpdateAtVal,
          Name: ProjectNameVal,
          Order: 1,
          ProjectId: 0,
          ProjectName: null,
          Type: ProjectNameVal,
          TypeId: 9,
          isOnLine: false,
        };
        objBeforeSearchNew.push(ObjBeforeSearch);
        localStorage.setItem('C2_BeforeSearchObjectProject', JSON.stringify(objBeforeSearchNew));
      }

      const isInfoWindow = this.props.isInfowindowViewCard;
      const corgeeFunctions = this.props.corgeeFunctions;
      $(this.refs[element])
        .find('a.originalConversationLink')
        .on('click', (e: any) =>
        {
          e.preventDefault();
          if (isInfoWindow)
          {
            const linkedId = normalizeConversationId(
              e.currentTarget.dataset.linkedConversationid,
            );
            if (linkedId) corgeeFunctions?.openInfoWindow?.(linkedId as any);
          }
          else
          {
            window.open(
              window.location.origin +
                import.meta.env.BASE_URL +
                'Dashboard/ConversationDetails/#/' +
                e.currentTarget.dataset.linkedConversationid,
              '_blank',
            );
          }
        });
    }
  }

  RemeasureSeeMoreLink(): void
  {
    const element = 'cardElementDescription_' + this.props.conversation.ConversationId;
    const ref = this.refs[element] as any;
    if (!ref) return;

    const cardContainer = ref.querySelector('.card-content-container-height-listener');
    if (!cardContainer) return;

    const finalHeight = cardContainer.scrollHeight;
    if (finalHeight > 137 && this.hasVisibleContent(cardContainer))
    {
      this.setState({ showSeeMoreLink: true });
    }
    else
    {
      this.setState({ showSeeMoreLink: false });
    }

    this.countImages(ref, finalHeight);
  }

  hasVisibleContent(container: Element): boolean
  {
    const containerTop = container.getBoundingClientRect().top;
    let lastContentBottom = 0;

    // Walk every text node in the tree and find the lowest one with real content
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node)
    {
      if ((node.textContent || '').trim().length > 0)
      {
        const range = document.createRange();
        range.selectNode(node);
        const bottom = range.getBoundingClientRect().bottom - containerTop;
        if (bottom > lastContentBottom) lastContentBottom = bottom;
      }
      node = walker.nextNode();
    }

    // Also account for media elements (images, video, etc.)
    container.querySelectorAll('img, video, iframe, table').forEach(el =>
    {
      const bottom = el.getBoundingClientRect().bottom - containerTop;
      if (bottom > lastContentBottom) lastContentBottom = bottom;
    });

    return lastContentBottom > 137;
  }

  countImages(html: any, offsetHeight: number): void
  {
    let ImageLoadCounter = 0;
    const Images = $(html).find('img');

    if (Images && Images.length > 0)
    {
      for (let m = 0; m < Images.length; m++)
      {
        const imgElement = Images[m];
        if (!imgElement) continue;

        const $img = $(imgElement);

        // Skip if already handled to prevent multiple bindings
        if ($img.data('events-bound')) continue;
        $img.data('events-bound', true);

        $img.on('load', () =>
        {
          ImageLoadCounter++;
          if (ImageLoadCounter == Images.length)
          {
            const cardContainer = $(html).find('.card-content-container-height-listener');
            if (cardContainer.length > 0 && cardContainer[0])
            {
              const finalHeight = cardContainer[0].scrollHeight;

              if (finalHeight > 137 && this.hasVisibleContent(cardContainer[0]))
              {
                this.setState({ showSeeMoreLink: true });
              }
              else
              {
                this.setState({ showSeeMoreLink: false });
              }
            }
          }
        });

        // Use .one() for error to prevent retriggering on the same broken image
        $img.one('error', () =>
        {
          ImageLoadCounter++;
          // Mark as handled so we don't rebind
          $img.data('error-handled', true);
          if (ImageLoadCounter == Images.length)
          {
            const cardContainer = $(html).find('.card-content-container-height-listener');
            if (cardContainer.length > 0 && cardContainer[0])
            {
              const finalHeight = cardContainer[0].scrollHeight;

              if (finalHeight > 137 && this.hasVisibleContent(cardContainer[0]))
              {
                this.setState({ showSeeMoreLink: true });
              }
              else
              {
                this.setState({ showSeeMoreLink: false });
              }
            }
          }
        });
      }
    }
    else
    {
      const plainContainer = $(html).find('.card-content-container-height-listener')[0];
      if (offsetHeight > 137 && plainContainer && this.hasVisibleContent(plainContainer))
      {
        this.setState({ showSeeMoreLink: true });
      }
      else
      {
        this.setState({ showSeeMoreLink: false });
      }
    }

    $(html)
      .find('.card-content-container-height-listener')
      .bind('contextmenu', (event: any) =>
      {
        event.preventDefault();
      });

    $('.liConversationCard')
      .find('.attachments-img')
      .bind('contextmenu', (event: any) =>
      {
        event.preventDefault();
      });
  }

  updateDimensions(): void
  {
    if (this.props.isInfowindowViewCard && this.cardBodyRef)
    {
      const rect = this.cardBodyRef.getBoundingClientRect();
      const height = Math.max(0, window.innerHeight - rect.top);
      this.setState({ cardBodyheight: height + 'px' });
      return;
    }

    const height = (this.refs.cardElementHeader as any)?.refs?.cardElement?.clientHeight || 0;
    let headerlayer = 0;
    if ($('.table-layout.core-header') && $('.table-layout.core-header').length > 0)
    {
      headerlayer = $('.table-layout.core-header').height() || 0;
    }
    const infoWindowOffset = this.props.isInfowindowViewCard ? 55 : 0;

    this.setState({
      cardBodyheight: window.innerHeight - height - 1 - headerlayer - infoWindowOffset + 'px',
    });
  }

  componentWillUnmount(): void
  {
    this.detachCardBodyScrollLeftPin();

    if (this._phaseLoadingTimeout) clearTimeout(this._phaseLoadingTimeout);

    if (this._seeMoreResizeObserver)
    {
      this._seeMoreResizeObserver.disconnect();
      this._seeMoreResizeObserver = null;
    }

    if (window.removeEventListener)
    {
      window.removeEventListener('resize', this.updateDimensions);
    }
    else if ((window as any).detachEvent)
    {
      (window as any).detachEvent('onresize', this.updateDimensions);
    }

    document.removeEventListener('keyup', this.catchEventsTabs);
    window.removeEventListener('corgee:conversation-phases-updated', this.handlePhasesUpdated);
    window.removeEventListener('corgee:phases-created', this.handlePhasesCreated);

    // Unsubscribe from conversationBus events
    if (this.unsubReplyAdded)
    {
      this.unsubReplyAdded();
    }
    if (this.unsubLayerUpdated)
    {
      this.unsubLayerUpdated();
      this._hasRegisteredLayerListener = false;
    }

    clearTimeout(this.highlightCardTimeout);
    if (this.highlightCardRafId !== undefined)
    {
      cancelAnimationFrame(this.highlightCardRafId);
    }
  }

  getClickHandler(
    onClick: (e: any) => void,
    onDblClick: (e: any) => void,
    delay?: number,
  ): (event: any) => void
  {
    let timeoutID: any = null;
    delay = delay || 250;
    return (event: any) =>
    {
      if (!timeoutID)
      {
        timeoutID = setTimeout(() =>
        {
          onClick(event);
          timeoutID = null;
        }, delay);
      }
      else
      {
        timeoutID = clearTimeout(timeoutID);
        onDblClick(event);
      }
    };
  }

  updateLinkConversationsByView(): void
  {
    const element_test = 'cardElementDescription_' + this.props.conversation.ConversationId;
    const cardContainer = this.refs[element_test].querySelector(
      '.card-content-container-height-listener',
    );

    let linkedConversationsbyReply: NodeListOf<HTMLAnchorElement>;

    if (cardContainer !== null && cardContainer !== undefined)
    {
      linkedConversationsbyReply = cardContainer.querySelectorAll('a[link-conversation-id]');

      if (linkedConversationsbyReply.length > 0)
      {
        linkedConversationsbyReply.forEach(item =>
        {
          item.classList.add('info-window-linkedItem');
          item.removeAttribute('href');
          item.removeAttribute('target');
          item.removeEventListener('click', () => {});
          item.addEventListener(
            'click',
            this.getClickHandler(
              (e: any) =>
              {
                e.preventDefault();
                const linkedId = normalizeConversationId(
                  item.getAttribute('link-conversation-id'),
                );
                if (linkedId && (this.props.corgeeFunctions as any)?.openInfoWindow)
                {
                  (this.props.corgeeFunctions as any).openInfoWindow(linkedId);
                }
              },
              (e: any) =>
              {
                e.preventDefault();
              },
            ),
          );
        });
      }
    }
  }

  catchEventsTabs(event: KeyboardEvent): void
  {
    if ($(this.refs.tabsH).find('.div-renamePhases').length > 0)
    {
      if (event.keyCode == 27)
      {
        this.handleChange(null);
      }
    }
  }

  componentDidMount(): void
  {
    this.attachCardBodyScrollLeftPin();

    if (window.addEventListener)
    {
      window.addEventListener('resize', this.updateDimensions);
    }
    else if ((window as any).attachEvent)
    {
      (window as any).attachEvent('onresize', this.updateDimensions);
    }

    document.addEventListener('keyup', this.catchEventsTabs);

    // Listen for phase updates for this specific conversation
    window.addEventListener('corgee:conversation-phases-updated', this.handlePhasesUpdated);
    window.addEventListener('corgee:phases-created', this.handlePhasesCreated);

    // Listen for reply-added events from external sources (InfoWindow, SubmitTime, etc.)
    this.unsubReplyAdded = conversationBus.on('reply-added', data =>
    {
      if (data.conversationId === this.props.conversation.ConversationId)
      {
        if (data.reply && data.reply.ReplyId > 0)
        {
          // If this card already has the reply (added through its own handleAddReply), skip.
          // Otherwise add it smoothly without the flash caused by reloadReplies.
          // recentlyConfirmedReplyIds catches the card's own echo of a reply it just added:
          // ConversationCardAddReply emits this same event synchronously right after calling
          // handleAddReply, before props.conversation.Replies has re-rendered to include it,
          // so the props-based check below can still miss it on a false negative.
          const alreadyHandled = this.recentlyConfirmedReplyIds.has(data.reply.ReplyId);
          const existingReplies = this.props.conversation.Replies || [];
          const alreadyExists = alreadyHandled || existingReplies.some(
            (r: any) => r.ReplyId === data.reply.ReplyId && !r.TemporalReply,
          );
          if (!alreadyExists)
          {
            this.handleAddReply(data.conversationId, data.reply, true, false);
            // handleAddReply(isUpdate=true) doesn't increment totalReplies, so do it here.
            // Only count regular replies (OriginalConversationId === 0), not reposts/tasks.
            if (!data.reply.OriginalConversationId)
            {
              this.setState(prev => ({ totalReplies: (prev.totalReplies || 0) + 1 }));
            }
          }
        }
        else
        {
          // No reply payload — fall back to a full reload (legacy events from older callers)
          this.reloadReplies(this.state.tabIndex);
        }
      }
    });

    // Auto-switch to updated phase when field modified in another view
    // Prevent duplicate listener registration (React StrictMode, remounts, etc.)
    if (!this._hasRegisteredLayerListener)
    {
      this._hasRegisteredLayerListener = true;

      this.unsubLayerUpdated = conversationBus.on('layer-updated', data =>
      {
        if (
          data.conversationId === this.props.conversation.ConversationId &&
          data.coreTaskId &&
          this.props.conversation.HasPhases &&
          this.state.phaseSelected
        )
        {
          const currentCoreTaskId =
            this.state.phaseSelected.CoreTaskId ??
            (this.state.phaseSelected as any).CoreTaskID;

          // Switch to updated phase if different from current
          if (
            data.coreTaskId !== currentCoreTaskId &&
            data.conversationPhaseId !== this.state.phaseSelected.ConversationPhaseId
          )
          {
            const updatedPhaseIndex = this.state.Phases.findIndex(
              phase =>
                (phase.CoreTaskId ?? (phase as any).CoreTaskID) === data.coreTaskId ||
                phase.ConversationPhaseId === data.conversationPhaseId,
            );

            if (updatedPhaseIndex >= 0)
            {
              setTimeout(() =>
              {
                const tabToClick = this.refs.tabsH?.querySelector(
                  `[data-conversationphaseid="${data.conversationPhaseId}"]`,
                );
                if (tabToClick)
                {
                  (tabToClick as HTMLElement).click();
                }
              }, 50);
            }
          }
        }
      });
    }

    this.updateDimensions();

    setTimeout(() =>
    {
      const element = 'cardElementDescription_' + this.props.conversation.ConversationId;
      const cardContainer =
        this.refs[element] != undefined &&
        this.refs[element].querySelector('.card-content-container-height-listener') != undefined
          ? this.refs[element].querySelector('.card-content-container-height-listener')
          : null;

      if (this.refs[element] && cardContainer)
      {
        const finalHeight = cardContainer.scrollHeight;

        if (finalHeight > 137 && this.hasVisibleContent(cardContainer))
        {
          this.setState({ showSeeMoreLink: true, displayFullContent: false });
        }
        else
        {
          this.setState({ showSeeMoreLink: false });
        }

        this.openLinkOnNewWindow();
        this.countImages(this.refs[element], finalHeight);

        // Re-evaluate when container width changes (reflow changes text layout)
        if (typeof ResizeObserver !== 'undefined')
        {
          this._lastObservedWidth = cardContainer.clientWidth;
          this._seeMoreResizeObserver = new ResizeObserver(entries =>
          {
            for (const entry of entries)
            {
              const newWidth = Math.round(entry.contentRect.width);
              if (newWidth !== this._lastObservedWidth)
              {
                this._lastObservedWidth = newWidth;
                this.RemeasureSeeMoreLink();
              }
            }
          });
          this._seeMoreResizeObserver.observe(cardContainer);
        }
      }

      this.onShowArrows();
    }, 300);

    // Linked cards must open in the InfoWindow on every view, ConversationDetails included
    this.updateLinkConversationsByView();

    if (this.props.selectedPhaseId)
    {
      setTimeout(() =>
      {
        const tabToClick = this.refs.tabsH.querySelector(
          '[data-conversationphaseid="' + this.props.selectedPhaseId + '"]',
        );
        if (tabToClick)
        {
          (tabToClick as HTMLElement).click();
        }
      }, 0);
    }
    else if (this.props.initialTabIndex && this.props.initialTabIndex > 0)
    {
      this.reloadReplies(this.props.initialTabIndex);
    }
  }

  onOpenInfoWindow(): void
  {
    (this.props.corgeeFunctions as any)?.openInfoWindow(this.props.conversation);
  }

  onChangeSummernote(content: string): void
  {
    this.setState({ bodyEdited: '<div>' + content + '</div>' });
  }

  onMarkAsImportantBody(e: React.MouseEvent): void
  {
    e.preventDefault();
    const { conversation } = this.props;
    const newImportant = this.state.isImportant;

    this.setState({ isImportant: newImportant }, () =>
    {
      setTimeout(() =>
      {
        if (this.props.onUpdateConversation)
        {
          this.props.onUpdateConversation(
            this.props.conversation.ConversationId,
            { IsImportant: newImportant },
            true,
            'header',
          );
        }
      }, 0);

      const requestParams: any = {
        conversations: [
          {
            ConfigValueId: conversation.ConfigValueId,
            ConversationId: conversation.ConversationId,
            CoreTaskId: conversation.CoreTaskId,
            CreatedByID: conversation.CreatedByID,
            Finished: conversation.Finished,
            Icon: conversation.Icon,
            IsImportant: !this.state.isImportant,
            ProjectId: conversation.ProjectId,
            SideMenuText: conversation.SideMenuText,
            Title: conversation.Title,
            UpdatedAt: conversation.UpdatedAt,
            callService: true,
          },
        ],
        SocketConnectionId:
          this.props.SocketConnectionId != undefined && this.props.SocketConnectionId != null
            ? this.props.SocketConnectionId
            : '',
      };

      if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarStart)
      {
        this.props.corgeeFunctions.loadingBarStart();
      }

      ConversationApi.updateImportant(this.props.api, requestParams)
        .then(() =>
        {
          if (this.props.corgeeFunctions && this.props.corgeeFunctions.loadingBarComplete)
          {
            this.props.corgeeFunctions.loadingBarComplete();
          }
          const conversationsIds = requestParams.conversations
            .map((obj: any) => obj.ConversationId)
            .join(',');
          ConversationApi.SetConversationsAsViewed(this.props.api, {
            ConversationIds: conversationsIds,
          }).then(() => {});

          if (this.props.onUpdateConversation)
          {
            const newAttrs: Partial<ConversationDetail> = {
              UpdatedBy: this.props.userData?.personName,
              UpdatedAt: new Date().toISOString(),
            };
            this.props.onUpdateConversation(
              this.props.conversation.ConversationId,
              newAttrs,
              true,
              'header',
            );
          }
        })
        .catch(error =>
        {
          console.error(error);
        });
    });
  }

  onUpdateTemporalImportant(newImportant: boolean): void
  {
    this.setState({ isImportant: newImportant });
  }

  onKeyDownEditor(e: React.KeyboardEvent): void
  {
    if (e.keyCode == 27)
    {
      this.closeEditor();
    }
  }

  closeEditor(deleteDraft?: boolean): void
  {
    switch (deleteDraft)
    {
      case true:
        {
          this.setState({ enableEditBody: false, showBodyIsRequired: false });
          this.updateLinkConversationsByView();
          this.setState({
            enableEditBody: false,
            showBodyIsRequired: false,
            showConfirmationModal: false,
          });
        }
        break;

      case false:
        {
          this.setState({ showConfirmationModal: false });
        }
        break;

      default:
        {
          if (
            $(this.state.bodyEdited as string).text().length > 0 ||
            $(this.state.bodyEdited as string).find('img').length > 0
          )
          {
            const existingStyles = $('.ui-dialog.react-draggable').attr('style');
            if (existingStyles && !existingStyles.includes('z-index:8'))
            {
              $('.ui-dialog.react-draggable').attr(
                'style',
                existingStyles + 'z-index:8 !important',
              );
            }
            this.setState({ showConfirmationModal: true });
          }
          else
          {
            this.closeEditor(true);
          }
        }
        break;
    }
  }

  onSaveEditBody(e: React.MouseEvent): void
  {
    // Ignore clicks while the update request is in flight
    if (this.state.isSavingBody)
    {
      e.preventDefault();
      return;
    }

    if (this.state.isImportant !== this.props.conversation.IsImportant)
    {
      this.onMarkAsImportantBody(e);
    }
    else
    {
      e.preventDefault();
    }

    // Read from the editor; onChangeSummernote wraps it in a <div>, so match that.
    const liveBody = this.summernoteCorgeeElement?.getContent?.();
    const editedBody =
      typeof liveBody === 'string' ? '<div>' + liveBody + '</div>' : this.state.bodyEdited || '';
    const newBody = editedBody.replace('&amp;', '&');
    void $('<div>' + newBody + '</div>').text();
    const requestParams: any = {
      ConversationId: this.props.conversation.ConversationId,
      Body: newBody,
      Tags: this.getTagsInBody(newBody),
      SocketConnectionId: this.props.SocketConnectionId,
    };
    requestParams.Body = this.disableChecklist(requestParams.Body);

    if (requestParams.Body === '<p><br></p>')
    {
      requestParams.Body = '<span></span>';
    }

    this.setState({ isSavingBody: true });

    ConversationApi.updateBody(this.props.api, requestParams)
      .then((response: any) =>
      {
        this.setState({
          body: response.Body,
          isSavingBody: false,
          enableEditBody: false,
          textHeight: 170,
          showBodyIsRequired: false,
        });
        let showSeeMoreLink = false;
        const element = 'cardElementDescription_' + this.props.conversation.ConversationId;

        setTimeout(() =>
        {
          if (this.refs[element])
          {
            const cardContainer =
              this.refs[element] != undefined &&
              this.refs[element].querySelector('.card-content-container-height-listener') != undefined
                ? this.refs[element].querySelector('.card-content-container-height-listener')
                : null;

            if (cardContainer !== null)
            {
              const finalHeight = cardContainer.scrollHeight;

              if (finalHeight > 137 && this.hasVisibleContent(cardContainer))
              {
                showSeeMoreLink = true;
              }
              else
              {
                showSeeMoreLink = false;
              }
            }
          }

          if (this.props.onUpdateConversation)
          {
            const newAttrs: Partial<ConversationDetail> = {
              Body: response.Body,
              Tags: response.Tags,
              UpdatedBy: response.UpdatedBy,
              UpdatedAt: response.UpdatedAt,
            };
            this.props.onUpdateConversation(
              this.props.conversation.ConversationId,
              newAttrs,
              true,
              'body',
            );
          }
        }, 600);

        ConversationApi.SetConversationsAsViewed(this.props.api, {
          ConversationIds: String(this.props.conversation.ConversationId),
        }).then((response: any) =>
        {
          this.setState({
            updatedBy: response.UpdatedBy,
            showSeeMoreLink: showSeeMoreLink,
          });
        });

        setTimeout(() =>
        {
          try
          {
            this.updateLinkConversationsByView();
          }
          catch (_e) {}
        }, 200);
      })
      .catch(error =>
      {
        // Re-enable the button so the user can retry
        this.setState({ isSavingBody: false });
        console.error('Error updating card body:', error);
      });
  }

  onEditBody(e: React.MouseEvent): void
  {
    e.preventDefault();
    const canEditAction = CorgeeHelper.convHasEditPermissions(this.props.conversation, null);
    const element = 'cardElementDescription_' + this.props.conversation.ConversationId;

    if (this.props.conversation.ConfigValueId === 15 || !canEditAction)
    {
      return;
    }

    if (this.state.enableEditBody === false && this.refs[element])
    {
      const cardContainer =
        this.refs[element] != undefined &&
        this.refs[element].querySelector('.card-content-container-height-listener') != undefined
          ? this.refs[element].querySelector('.card-content-container-height-listener')
          : null;
      let textHeight = cardContainer?.offsetHeight || 170;

      if (textHeight < 170)
      {
        textHeight = 170;
      }
      this.setState({ enableEditBody: true, textHeight: textHeight });
    }

    this.setState({ isImportant: this.props.conversation.IsImportant });
  }

  onCancelEditBody(): void
  {
    this.setState({
      enableEditBody: false,
      textHeight: 170,
      showBodyIsRequired: false,
      isSavingBody: false,
    });

    setTimeout(() =>
    {
      this.updateLinkConversationsByView();
    }, 200);
  }

  onSeeMoreContent(e: React.MouseEvent): void
  {
    e.preventDefault();
    this.setState({ displayFullContent: !this.state.displayFullContent });
  }

  getTagsInBody(bodyTags: string): any[]
  {
    try
    {
      const listTags: any[] = [];
      const taggedPeople = $('<div>' + bodyTags + '</div>').find('span[person-id]');
      for (let i = 0; i < taggedPeople.length; i++)
      {
        listTags.push({
          PersonId: parseInt($(taggedPeople[i] as any).attr('person-id') || '0'),
          ConversationId: this.props.conversation.ConversationId,
          ReplyId: 0,
          ReplyFollowing: 0,
        });
      }
      return listTags;
    }
    catch (err)
    {
      console.warn(err);
      return [];
    }
  }

  disableChecklist(content: string): string
  {
    const div = document.createElement('div');
    div.innerHTML = content;
    const checkboxes = $(div).find('.note-editor-checkbox');
    $.each(checkboxes, (_index: number, value: any) =>
    {
      $(value).attr('disabled', 'true');
    });
    content = div.innerHTML;

    try
    {
      $(content);
    }
    catch (err)
    {
      content = content.replace('<p><br></p>', '');
      content = '<p>' + content + '</p>';
    }

    return content;
  }

  onSubmitTime(): void
  {
    this.setState({ submitTime: true });
    setTimeout(() =>
    {
      this.setState({ submitTime: false });
    });

    if (this.props.onSubmitTime)
    {
      this.props.onSubmitTime(this.props.conversation);
    }
  }

  // ==================== Edit History ====================

  onOpenEditHistory(customField: any, onSaveModal: any, onUpdateModal: any): void
  {
    this.setState({
      edithistory: {
        open: true,
        title: customField.Name,
        customField: customField,
        onSaveModal: onSaveModal,
        onEditModal: onUpdateModal,
      },
      customFieldObject: customField,
    });
  }

  onCloseEditHistory(): void
  {
    this.setState({
      edithistory: {
        open: false,
        title: '',
        customField: [],
        onSaveModal: null,
        onEditModal: null,
      },
    });
  }

  updateCustomFieldsHistory(customField: any): void
  {
    const customfieldslayers = (this.props.conversation as any).layerCustomFields
      ? (this.props.conversation as any).layerCustomFields
      : [];

    if (customfieldslayers.length > 0)
    {
      for (let x = 0; x < customfieldslayers.length; x++)
      {
        if (customfieldslayers[x].LayerId == customField.LayerId)
        {
          for (let y = 0; y < customfieldslayers[x].CustomFields.length; y++)
          {
            if (customfieldslayers[x].CustomFields[y].CustomFieldId == customField.CustomFieldId)
            {
              customfieldslayers[x].CustomFields[y] = customField;
            }
          }
        }
      }
      this.props.UpdateCustomFieldsInternal?.(
        this.props.conversation.ConversationId,
        customfieldslayers,
      );
      this.setState({ customFieldObject: customField });
    }
  }

  onHandleReplyTabSelected(tabName: ReplyTab): void
  {
    this.setState({ selectedReplyTab: tabName }, () =>
    {
      if (tabName === 'metrics')
      {
        this.loadMetrics();
      }
      else
      {
        this.reloadReplies(this.state.tabIndex);
      }
    });
  }

  loadMetrics(): void
  {
    const { conversation, api } = this.props as any;

    const layerMap: Record<number, { Name: string; Color: string }> = {};
    try
    {
      const cached = sessionStorage.getItem('C2_AllLayers');
      if (cached)
      {
        const layers: { LayerId: number; Name: string; Color: string }[] = JSON.parse(cached);
        layers.forEach(l => { layerMap[l.LayerId] = { Name: l.Name, Color: l.Color }; });
      }
    }
    catch { /* ignore */ }

    MenuApi.getMetrics(api, conversation.ConversationId)
      .then((data: any) =>
      {
        const metrics = (data?.metricList || []).map((m: any) =>
        {
          const layer = layerMap[m.LayerId];
          return {
            ...m,
            LayerName: layer?.Name || m.LayerName || `Layer ${m.LayerId}`,
            LayerColor: layer?.Color || m.LayerColor || '#e0e0e0',
          };
        });
        this.setState({ metrics });
      })
      .catch(() =>
      {
        this.setState({ metrics: [] });
      });
  }

  activaDarkMode(): void
  {
    const darkMode = localStorage.getItem('C2_enableDarkMode');
    if (darkMode == 'true')
    {
      if (document.documentElement.classList.value == 'k-webkit k-webkit90 ng-scope')
      {
        document.documentElement.classList.value = 'k-webkit k-webkit90 ng-scope dark-mode';
      }
    }
  }

  handleChangeTabs(index: number, _lastIndex: number, event: any): boolean | void
  {
    const tabsH = this.refs.tabsH;
    const IsTabsOpen = tabsH.getElementsByClassName('div-renamePhases');

    if (IsTabsOpen.length > 0)
    {
      return false;
    }

    if ($('#input_0').length > 0)
    {
      return;
    }

    if (event.target.classList.contains('closetab'))
    {
      return;
    }

    this.setState({ tabIndex: index });
    this.props.onTabChange?.(index);

    if (event.detail == 1 || event.detail == 0)
    {
      if (this.waitTime != 0) clearTimeout(this.waitTime);

      this.waitTime = setTimeout(() =>
      {
        this.waitTime = 0;
        this.reloadReplies(index);
      }, 800);
    }
    else if (event.detail == 2)
    {
      clearTimeout(this.waitTime);
      this.waitTime = 0;

      if (index > 0)
      {
        const phases = this.state.Phases;
        const indexfix = index - 1;

        for (let a = 0; a < phases.length; a++)
        {
          (phases[a] as any).Edit = false;
        }

        const phaseAtFix = phases[indexfix];
        if (phaseAtFix) (phaseAtFix as any).Edit = true;
        const inputValue = phases[indexfix]?.Title || '';
        const inputIndex = indexfix;

        let blank: SelectOption = { label: '', value: '' };

        if ((phases[indexfix] as any)?.PhaseId > 0)
        {
          blank = { label: inputValue, value: (phases[indexfix] as any).PhaseId };
        }

        this.loadCatPhases();

        this.setState(
          {
            Phases: phases,
            inputValue: inputValue,
            inputIndex: inputIndex,
            cmbSelect: blank,
            phaseMenuOpen: true,
          },
          () =>
          {
            $('#input_' + phases[indexfix]?.ConversationPhaseId).focus();
            $('#input_' + phases[indexfix]?.ConversationPhaseId).select();

            const tabsH = this.refs.tabsH;
            if (tabsH.getElementsByClassName('react-tabs__tab--selected').length > 0)
            {
              const tabs = tabsH.getElementsByClassName('react-tabs__tab--selected')[0];
              tabs.setAttribute(
                'style',
                'bottom:-1px!important; padding-top: 0px; padding-bottom: 2px;',
              );
            }

            this.onShowArrows();

            const initialScroll2 = $(tabsH).closest('.sortableDashboard').scrollTop() || 0;
            $(tabsH)
              .closest('.sortableDashboard')
              .on('scroll', (_e: any) =>
              {
                const newScroll2 = $(tabsH).closest('.sortableDashboard').scrollTop() || 0;
                if (initialScroll2 != newScroll2)
                {
                  this.handleChange(null);
                }
              });

            $(tabsH)
              .closest('.layer-custom-fields-container')
              .on('scroll', (_e: any) =>
              {
                this.handleChange(null);
              });

            const initialScrollBodyInfo =
              $(tabsH).closest('.corgee_com_card-body-info').scrollTop() || 0;
            $(tabsH)
              .closest('.corgee_com_card-body-info')
              .on('scroll', (_e: any) =>
              {
                const actualScrollBodyInfo =
                  $(tabsH).closest('.corgee_com_card-body-info').scrollTop() || 0;
                if (initialScrollBodyInfo != actualScrollBodyInfo)
                {
                  this.handleChange(null);
                }
              });

            const initialScroll7 = $(tabsH).closest('.core2').scrollTop() || 0;
            setTimeout(() =>
            {
              $('.core2').on('scroll', (_e: any) =>
              {
                const newScroll7 = $(tabsH).closest('.core2').scrollTop() || 0;
                if (initialScroll7 != newScroll7)
                {
                  this.handleChange(null);
                }
              });
            });
          },
        );
      }
    }
  }

  deepEqual(object1: any, object2: any): boolean
  {
    if (object1 == null && object2 == null) return true;
    if (object1 == null || object2 == null) return false;
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length)
    {
      return false;
    }

    for (const key of keys1)
    {
      if (key != 'Highlight' && key != 'Edit')
      {
        const val1 = object1[key];
        const val2 = object2[key];
        const areObjects = this.isObject(val1) && this.isObject(val2);
        if ((areObjects && !this.deepEqual(val1, val2)) || (!areObjects && val1 !== val2))
        {
          return false;
        }
      }
    }
    return true;
  }

  isObject(object: any): boolean
  {
    return object != null && typeof object === 'object';
  }

  // ==================== Render ====================

  render()
  {
    const { conversation, isConversationDetailsView, isInfowindowViewCard } = this.props;
    const {
      submitTime,
      seeMoreReplies,
      totalFavorites,
      totalReplies,
      openRepost,
      itemRepost,
      requestConfig,
      textDetailsSection,
      showDetails,
      pageNumber,
      pageSize,
      isFromAddReply,
      highlight,
      highlightHeader,
      highlightCard,
      highlightAttachment,
      reactSelfUpdate,
      openGallery,
      selectedImageId,
      embeddedImages,
      highlightSM,
      repliesPendingToHighlight,
      firstLayerColor,
      tabIndex,
      enablePhases,
      deletePhase,
      options,
      cmbSelect,
      updateMessageMoreReplies,
      convImportant,
      selectedReplyTab,
      edithistory,
    } = this.state;

    const onOpenEditHistory = this.onOpenEditHistory;
    const updateCustomFieldsHistory = this.updateCustomFieldsHistory;
    const openModalPhase = this.openModalPhase;
    const handleChange = this.handleChange;
    const handleOpen = this.handleOpen;
    const CreateNewOption = this.CreateNewOption;
    const selectedPhase = cmbSelect;

    const isDark = document.documentElement.classList.contains('dark');
    const backgroundColor = isDark ? '#3d3a2a' : '#ffffe1';
    const originalBackgroundColor = isDark ? '#333535' : '#ffffff';
    const selectStyles = getSelectStyles(isDark);
    this.activaDarkMode();

    const CustomTab = ({ onClick, children }: any) =>
    {
      return <div onClick={onClick}>{children}</div>;
    };
    (CustomTab as any).tabsRole = 'Tab';

    const {
      displayFullContent,
      enableEditBody,
      isSavingBody,
      body,
      showBodyIsRequired,
      textHeight,
      showSeeMoreLink,
      HasPhases,
      Phases,
      validateInputValue,
      descriptionInputValue,
      showArrows,
      top,
      left,
      showConfirmationModal,
    } = this.state;

    // Use cached value - only recalculate if Body changed
    if (this.cachedBody !== conversation.Body)
    {
      this.cachedBody = conversation.Body;
      this.cachedShowSeeMoreContent = CorgeeHelper.showSeeMoreContent(
        conversation.Body,
        conversation.CreatedBy,
      );
    }
    const showSeeMoreContent = this.cachedShowSeeMoreContent;
    const cardToggleContent = {
      CssClass: 'display-full-content',
      Text: 'See Less',
    };
    let classArrow = 'arrow_drop_up';

    if ((!displayFullContent || !showSeeMoreLink) && enableEditBody === false)
    {
      cardToggleContent.CssClass = 'hide-full-content';
      cardToggleContent.Text = 'See More';
      classArrow = 'arrow_drop_down';
    }

    let htmlBody = '<p><br></p>';
    const convWithInternalBody: any = Object.assign({}, conversation);
    convWithInternalBody.Body = body;
    htmlBody = addTargetBlankToLinks(CorgeeHelper.getBodyValidated(convWithInternalBody));
    function createMarkup()
    {
      return { __html: htmlBody };
    }

    const cardbodyStyle = !isInfowindowViewCard
      ? { height: 'auto' }
      : { height: this.state.cardBodyheight };

    const styleMessage = {
      top: top,
      left: left,
    };

    return (
      <div
        className={
          (conversation as any).Highlight || highlightCard
            ? 'corgee_com_card notification-highlight'
            : 'corgee_com_card'
        }
        style={{
          backgroundColor: (conversation as any).Highlight
            ? backgroundColor
            : originalBackgroundColor,
          position: 'relative',
        }}
      >
        {/* CARD HEADER */}
        <div className={highlightHeader ? 'react-item-highlighted notification-highlight' : ''}>
          <ConversationCardHeader
            {...(this.props as any)}
            convImportant={convImportant}
            reactSelfUpdate={reactSelfUpdate}
            ref={(ref: any) =>
            {
              this.refs.cardElementHeader = ref;
            }}
            onHighlight={this.state.highlightHeader}
            onSubmitTime={this.onSubmitTime}
            onAddReply={this.props.onAddReply}
            phaseSelected={this.state.phaseSelected}
          />
        </div>

        {/* CARD BODY */}
        <div
          className={
            'corgee_com_card-body' + (isInfowindowViewCard ? ' corgee_com_card-body-info' : '')
          }
          style={cardbodyStyle}
          ref={ref =>
          {
            this.cardBodyRef = ref;
          }}
        >
          <div
            className={
              'corgee_com_card-body-description ' +
              (highlight ? 'react-item-highlighted notification-highlight' : '')
            }
            ref={ref =>
            {
              this.refs['cardElementDescription_' + conversation.ConversationId] = ref;
            }}
          >
            {showSeeMoreContent === true && (
              <div
                className={
                  'corgee_com_card-truncated-body-description ' + cardToggleContent.CssClass
                }
              >
                <div
                  className="corgee_com_card-body-description-container"
                  onDoubleClick={this.onEditBody}
                >
                  {enableEditBody === false && (
                    <div
                      className="card-content-container-height-listener"
                      dangerouslySetInnerHTML={createMarkup()}
                      onClick={this.handleEmbeddedImagesClick}
                    ></div>
                  )}
                  {enableEditBody === true && (
                    <SummernoteCorgee
                      ref={(ref: any) => (this.summernoteCorgeeElement = ref)}
                      selectorContainer=".corgee_com_card-body-description-container"
                      conversationId={conversation.ConversationId}
                      isAddReply={false}
                      onChangeBody={this.onChangeSummernote}
                      onUpdateTemporalImportant={this.onUpdateTemporalImportant}
                      isImportant={conversation.IsImportant ?? undefined}
                      body={body}
                      layerColor={firstLayerColor}
                      textHeight={textHeight}
                      layers={conversation.ConversationLayers.filter(x => x.LayerId != 0)}
                      projectId={conversation.ProjectId}
                      configValueId={conversation.ConfigValueId}
                      onKeyDown={this.onKeyDownEditor as any}
                      {...this.props}
                    >
                      {showBodyIsRequired === true && (
                        <div className="corgee_com_validation-message">Enter a valid message.</div>
                      )}
                      <div className="corgee_com_card-publish-options">
                        <div className="corgee_com_card-publish-action">
                          <button
                            className="corgee_com_button corgee_com_button-primary btnChangeColorWithLayer"
                            onClick={this.onSaveEditBody}
                            disabled={isSavingBody}
                            style={{
                              backgroundColor: firstLayerColor,
                              borderColor: firstLayerColor,
                            }}
                          >
                            Update
                            {isSavingBody && (
                              <span
                                className="corgee_com_button-spinner"
                                role="status"
                                aria-label="Saving"
                              />
                            )}
                          </button>
                        </div>
                        <div className="corgee_com_card-publish-action">
                          <button
                            type="button"
                            className="corgee_com_button corgee_com_button-cancel"
                            onClick={this.onCancelEditBody}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </SummernoteCorgee>
                  )}
                </div>
                {enableEditBody === false && showSeeMoreLink === true && (
                  <div className="corgee_com_card-see-more-container">
                    <a
                      onClick={this.onSeeMoreContent}
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                      className="corgee_com_card-see-more-btn"
                    >
                      {cardToggleContent.Text}{' '}
                      <span className={'md-font material-icons'}>{classArrow}</span>
                    </a>
                  </div>
                )}
              </div>
            )}
            {showSeeMoreContent === false && (
              <div
                className="corgee_com_card-body-description-container"
                onDoubleClick={this.onEditBody}
              >
                {enableEditBody === false && (
                  <div
                    className="task-body-cardsview"
                    dangerouslySetInnerHTML={createMarkup()}
                    onClick={this.handleEmbeddedImagesClick}
                  />
                )}
                {enableEditBody === true && (
                  <SummernoteCorgee
                    ref={(ref: any) => (this.summernoteCorgeeElement = ref)}
                    selectorContainer=".corgee_com_card-body-description-container"
                    conversationId={conversation.ConversationId}
                    isAddReply={false}
                    onChangeBody={this.onChangeSummernote}
                    onUpdateTemporalImportant={this.onUpdateTemporalImportant}
                    isImportant={conversation.IsImportant ?? undefined}
                    body={body}
                    layerColor={firstLayerColor}
                    textHeight={textHeight}
                    layers={conversation.ConversationLayers.filter(x => x.LayerId != 0)}
                    projectId={conversation.ProjectId}
                    configValueId={conversation.ConfigValueId}
                    onKeyDown={this.onKeyDownEditor as any}
                    {...this.props}
                  >
                    {showBodyIsRequired === true && (
                      <div className="corgee_com_validation-message">Enter a valid message.</div>
                    )}
                    <div className="corgee_com_card-publish-options">
                      <div className="corgee_com_card-publish-action">
                        <button
                          className="corgee_com_button corgee_com_button-primary btnChangeColorWithLayer"
                          onClick={this.onSaveEditBody}
                          disabled={isSavingBody}
                          style={{ backgroundColor: firstLayerColor, borderColor: firstLayerColor }}
                        >
                          Update
                          {isSavingBody && (
                            <span
                              className="corgee_com_button-spinner"
                              role="status"
                              aria-label="Saving"
                            />
                          )}
                        </button>
                      </div>
                      <div className="corgee_com_card-publish-action">
                        <button
                          type="button"
                          className="corgee_com_button corgee_com_button-cancel"
                          onClick={this.onCancelEditBody}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </SummernoteCorgee>
                )}
              </div>
            )}
          </div>

          <div className="cancel-drag">
            {/* Attachments Section*/}
            {conversation.Attachments && conversation.Attachments.length > 0 && (
              <div className={highlightAttachment ? 'react-item-highlighted' : ''}>
                <AttachmentsContent
                  item={conversation}
                  isSingleReply="false"
                  attachments={conversation.Attachments}
                  corgeeFunctions={this.props.corgeeFunctions}
                  config={this.props.config}
                  api={this.props.api}
                />
              </div>
            )}

            {/* Phases */}
            {enablePhases && (
              <Tabs selectedIndex={tabIndex} onSelect={this.handleChangeTabs}>
                {HasPhases == true && showArrows && (
                  <div className="react-left-arrow-tabs" onClick={this.scrollLft}>
                    <i className="fa fa-chevron-left" aria-hidden="true"></i>
                  </div>
                )}
                {HasPhases == true && showArrows && (
                  <div className={'react-right-arrow-tabs'} onClick={this.scrollRight}>
                    <i className="fa fa-chevron-right" aria-hidden="true"></i>
                  </div>
                )}

                <TabList>
                  <div
                    ref={ref =>
                    {
                      this.refs.tabsH = ref;
                    }}
                    className={'tabsH-wrapper'}
                  >
                    <Tab
                      key={0}
                      className={'react-tabs__tab'}
                      {...({
                        index: 0,
                        'data-conversationphaseid': 0,
                        'data-coretaskid': conversation.CoreTaskId,
                      } as any)}
                    >
                      {HasPhases == true ? 'All Phases' : 'Add Phases'}
                    </Tab>

                    {Phases.map((Phase, i) =>
                    {
                      return (
                        <Tab
                          disabled={(Phase as any).Disabled}
                          className={'react-tabs__tab'}
                          key={i + 1}
                          {...({
                            index: i + 1,
                            'data-conversationphaseid': Phase.ConversationPhaseId,
                            'data-coretaskid': Phase.CoreTaskId,
                            title: Phase.Title,
                          } as any)}
                        >
                          {!(Phase as any).Edit && (
                            <div className="tabname">
                              {Phase.Title}
                              <span
                                className={'md-font material-icons closetab'}
                                onClick={e =>
                                  openModalPhase(
                                    e,
                                    Phase.Title || '',
                                    Phase.ConversationId,
                                    Phase.ConversationPhaseId,
                                  )
                                }
                              >
                                close
                              </span>
                            </div>
                          )}

                          {(Phase as any).Edit && (
                            <div className="div-renamePhases">
                              {React.createElement(Creatable as any, {
                                value: selectedPhase.value ? selectedPhase : null,
                                options: options.filter(
                                  x => x.isEnable && x.label.trim().length != 0,
                                ),
                                onChange: handleChange,
                                onMenuOpen: handleOpen,
                                // Controlled menu: survives re-renders, closes on select/blur/escape.
                                menuIsOpen: this.state.phaseMenuOpen,
                                onMenuClose: () => this.setState({ phaseMenuOpen: false }),
                                // Disable the combobox while the phase is being created.
                                isDisabled: this.state.isTabSaving || this.state.isCreatingPhase,
                                autoFocus: true,
                                placeholder: 'Select or create phase',
                                onCreateOption: (inputValue: string) =>
                                  CreateNewOption({ value: 0, label: inputValue, isEnable: true }),
                                getOptionLabel: (o: any) => o.label,
                                getOptionValue: (o: any) => String(o.value),
                                menuPosition: 'fixed',
                                menuPortalTarget: document.body,
                                styles: selectStyles,
                              })}
                              <label
                                className={validateInputValue ? 'showValidate' : 'hideValidate'}
                                style={styleMessage}
                              >
                                {descriptionInputValue}
                              </label>
                            </div>
                          )}
                        </Tab>
                      );
                    })}

                    <div
                      className="react-add-tabs enable-help-tooltip"
                      data-title="Add Phase - Note: When adding a phase the details of the main card will be overwritten."
                    >
                      {this.state.isTabSaving || this.state.isCreatingPhase ? (
                        // Creating a phase: show a loader instead of the + button
                        <span className="phase-add-loading" aria-label="Creating phase">
                          <span className="phase-add-loading__dot" />
                          <span className="phase-add-loading__dot" />
                          <span className="phase-add-loading__dot" />
                        </span>
                      ) : (
                        <span
                          className={'md-font material-icons'}
                          onClick={this.addtemporalphase}
                        >
                          add_circle_outline
                        </span>
                      )}
                    </div>
                  </div>
                </TabList>

                {isConversationDetailsView && (
                  <div className="corgee_com_card-hide-details-container">
                    <a className="corgee_com_card-hide-details-btn" onClick={this.setShowDetails}>
                      {textDetailsSection}
                    </a>
                  </div>
                )}

                {/* Phase Default  */}
                <TabPanel>
                  <div></div>
                </TabPanel>

                {/* More Phases  */}
                {Phases.map((Phase, i) =>
                {
                  return (
                    <TabPanel key={i + 1} {...({ index: Phase.ConversationPhaseId } as any)}>
                      <div></div>
                    </TabPanel>
                  );
                })}

                {(isConversationDetailsView || this.props.forceShowDetails) &&
                  showDetails &&
                  !this.props.hideCustomFields && (
                  <div>
                    {React.createElement(CustomFields as any, {
                      section: 'section',
                      ref: (ref: any) =>
                      {
                        (this.refs as any).CustomFields = ref;
                      },
                      config: requestConfig,
                      item: conversation,
                      projectId: conversation.ProjectId,
                      configValueId: conversation.ConfigValueId,
                      groups: conversation.ConversationGroups,
                      isConversationDetailsView: isConversationDetailsView,
                      UpdateCustomFields: this.props.UpdateCustomFields,
                      getForceReloadDetailsFn: this.props.getForceReloadDetailsFn,
                      submitTime: submitTime,
                      onOpenEditHistory: onOpenEditHistory,
                      updateCustomFieldsHistory: updateCustomFieldsHistory,
                      phaseSelected: this.state.phaseSelected,
                    })}
                  </div>
                )}

                <div>
                  <ConversationCardGrayBar
                    {...(this.props as any)}
                    seeMoreRepliesGrayBar={this.state.seeMoreRepliesGrayBar}
                    repliesFullyShown={this.state.repliesFullyShown}
                    highlightSM={highlightSM}
                    onSeeMoreReplies={this.onSeeMoreReplies}
                    totalFavorites={totalFavorites}
                    totalReplies={totalReplies}
                    updateMessageMoreReplies={updateMessageMoreReplies}
                    onHandleReplyTabSelected={this.onHandleReplyTabSelected as any}
                    isLoadingMoreReplies={this.state.isLoadingMoreReplies}
                  />

                  {/* Replies Section */}
                  {this.state.showReplies && selectedReplyTab !== 'metrics' && (
                    <ConversationCardReplies
                      ref={(ref: any) =>
                      {
                        (this.refs as any).ConversationCardReplies = ref;
                      }}
                      {...(this.props as any)}
                      onSubmitTime={this.onSubmitTime}
                      onRepliesWindowState={this.onRepliesWindowState}
                      resetSeeMoreReplies={this.resetSeeMoreReplies}
                      seeMoreReplies={seeMoreReplies}
                      onUpdateTotalFavorites={this.onUpdateTotalFavorites as any}
                      onDeleteReply={this.props.onDeleteReply as any}
                      onAddRepost={this.onAddRepost}
                      setShowRepost={this.setShowRepost}
                      onUpdateReply={this.onUpdateReply as any}
                      pageNumber={pageNumber}
                      pageSize={pageSize}
                      isFromAddReply={isFromAddReply}
                      setIsFromAddReply={this.setIsFromAddReply}
                      config={this.props.config}
                      isInfowindowViewCard={isInfowindowViewCard}
                      repliesPendingToHighlight={repliesPendingToHighlight}
                      removePendingHighlight={this.removePendingHighlight}
                      onResetRepliesList={this.onResetRepliesList as any}
                      submitTime={submitTime}
                      phaseSelected={this.state.phaseSelected}
                      selectedReplyTab={selectedReplyTab as any}
                      onLoadingMoreRepliesChange={this.setLoadingMoreReplies}
                    />
                  )}

                  {selectedReplyTab === 'metrics' && (
                    <ConversationCardMetrics
                      conversation={conversation}
                      metrics={this.state.metrics}
                    />
                  )}

                  {conversation.IsEnable && selectedReplyTab !== 'metrics' && (
                    <ConversationCardAddReply
                      {...(this.props as any)}
                      openRepost={openRepost}
                      itemRepost={itemRepost}
                      setShowRepost={this.setShowRepost}
                      setIsFromAddReply={this.setIsFromAddReply}
                      onAddLayerToCard={this.props.onAddLayerToCard}
                      onAddLayerToProject={this.addLayerToProjectFromReply}
                      onAddReply={this.handleAddReply}
                      phaseSelected={this.state.phaseSelected}
                    />
                  )}
                </div>
              </Tabs>
            )}

            {!enablePhases &&
              (isConversationDetailsView || this.props.forceShowDetails) &&
              showDetails &&
              !this.props.hideCustomFields && (
              <div>
                {React.createElement(CustomFields as any, {
                  section: 'section',
                  ref: (ref: any) =>
                  {
                    (this.refs as any).CustomFields = ref;
                  },
                  config: requestConfig,
                  item: conversation,
                  projectId: conversation.ProjectId,
                  configValueId: conversation.ConfigValueId,
                  groups: conversation.ConversationGroups,
                  isConversationDetailsView: isConversationDetailsView,
                  UpdateCustomFields: this.props.UpdateCustomFields,
                  getForceReloadDetailsFn: this.props.getForceReloadDetailsFn,
                  submitTime: submitTime,
                  onOpenEditHistory: onOpenEditHistory,
                  updateCustomFieldsHistory: updateCustomFieldsHistory,
                })}
              </div>
            )}

            {!enablePhases && (
              <div>
                <ConversationCardGrayBar
                  {...(this.props as any)}
                  seeMoreRepliesGrayBar={this.state.seeMoreRepliesGrayBar}
                  repliesFullyShown={this.state.repliesFullyShown}
                  highlightSM={highlightSM}
                  onSeeMoreReplies={this.onSeeMoreReplies}
                  totalFavorites={totalFavorites}
                  totalReplies={totalReplies}
                  onHandleReplyTabSelected={this.onHandleReplyTabSelected as any}
                  isLoadingMoreReplies={this.state.isLoadingMoreReplies}
                />

                {/* Replies Section */}
                {selectedReplyTab !== 'metrics' && (
                  <ConversationCardReplies
                    ref={(ref: any) =>
                    {
                      (this.refs as any).ConversationCardReplies = ref;
                    }}
                    {...(this.props as any)}
                    onSubmitTime={this.onSubmitTime}
                    onRepliesWindowState={this.onRepliesWindowState}
                    resetSeeMoreReplies={this.resetSeeMoreReplies}
                    seeMoreReplies={seeMoreReplies}
                    onUpdateTotalFavorites={this.onUpdateTotalFavorites as any}
                    onDeleteReply={this.props.onDeleteReply as any}
                    onAddRepost={this.onAddRepost}
                    setShowRepost={this.setShowRepost}
                    onUpdateReply={this.onUpdateReply as any}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    isFromAddReply={isFromAddReply}
                    setIsFromAddReply={this.setIsFromAddReply}
                    config={this.props.config}
                    isInfowindowViewCard={isInfowindowViewCard}
                    repliesPendingToHighlight={repliesPendingToHighlight}
                    removePendingHighlight={this.removePendingHighlight}
                    onResetRepliesList={this.onResetRepliesList as any}
                    submitTime={submitTime}
                    selectedReplyTab={selectedReplyTab as any}
                    onLoadingMoreRepliesChange={this.setLoadingMoreReplies}
                  />
                )}

                {selectedReplyTab === 'metrics' && (
                  <ConversationCardMetrics
                    conversation={conversation}
                    metrics={this.state.metrics}
                  />
                )}

                {conversation.IsEnable && selectedReplyTab !== 'metrics' && (
                  <ConversationCardAddReply
                    {...(this.props as any)}
                    openRepost={openRepost}
                    itemRepost={itemRepost}
                    setShowRepost={this.setShowRepost}
                    setIsFromAddReply={this.setIsFromAddReply}
                    onAddLayerToCard={this.props.onAddLayerToCard}
                    onAddLayerToProject={this.addLayerToProjectFromReply}
                    onAddReply={this.handleAddReply}
                  />
                )}
              </div>
            )}
          </div>

          <ImagesGallery
            open={openGallery}
            currentImageId={selectedImageId}
            attachments={embeddedImages.map(
              (img): GalleryAttachment => ({
                AttachmentId: img.AttachmentId || 0,
                EmbeddedImageId: img.EmbeddedImageId,
                FileUrl: img.FileUrl || '',
                Thumbnail: { FileUrl: img.Thumbnail?.FileUrl || '' },
                FileName: img.FileName || '',
                FileExtension: img.FileExtension || '',
                Favorite: img.Favorite,
                Description: img.Description,
                Title: img.Title,
                // Pass conversation context for default reply target
                ConversationId: this.props.conversation.ConversationId,
                ConversationTitle: this.props.conversation.Title,
                ProjectName: this.props.conversation.ProjectName,
                ProjectId: this.props.conversation.ProjectId,
              }),
            )}
            isEmbedded={true}
            onClose={this.onCloseGallery}
          />
        </div>

        <div className="show-details-panel"></div>

        {edithistory.open && (
          <EditDetailHistory
            customField={edithistory.customField as any}
            open={edithistory.open}
            api={this.props.api}
            title={edithistory.title}
            onClose={this.onCloseEditHistory}
            onSaveModal={edithistory.onSaveModal}
          />
        )}

        {this.state.openModal && (
          <CorgeeModal
            ref={ref =>
            {
              (this.refs as any).confirmModalPhases = ref;
            }}
            className={'confirmModalPhases'}
            openModal={this.state.openModal}
            onSave={this.removePhase}
            title={'Delete Phase'}
            onClose={this.closeModalPhase}
            isDraggable={true}
            closeOnEscape={true}
            hasMaxHeight={true}
            allowSaveAndNewCheckBox={false}
            hideActionButtons={false}
            width={430}
            height={120}
            textButton="Delete"
            saveButtonColor="#508ca6"
          >
            <div className="delete-phase-modal">
              {' '}
              Are you sure you want to delete this phase "{deletePhase.title}" ?{' '}
            </div>
          </CorgeeModal>
        )}

        <CustomModal show={showConfirmationModal}>
          <md-dialog
            className="md-transition-in"
            role="dialog"
            tabIndex={-1}
            style={{ opacity: '1', position: 'static' }}
            id="draftConfirmationModal"
          >
            <form className="">
              <md-toolbar style={{ cursor: 'default' }}>
                <div className="md-toolbar-tools">
                  <div className="popup-header-title ">{'Confirm Action'}</div>
                </div>
                <div
                  className="close-control modal-icon-close-pos"
                  role="button"
                  tabIndex={0}
                  onClick={() => this.closeEditor(false)}
                >
                  <i className="icon-incorrect-icon"></i>
                </div>
              </md-toolbar>
              <md-dialog-content style={{ display: 'table' }} id="dialog_23">
                <div className="md-dialog-content delete-item-modal">
                  Are you sure you want to delete? Draft will be missed.
                </div>
              </md-dialog-content>

              <md-dialog-actions className="layout-row">
                <span className="flex"></span>
                <button
                  className="modal-window-button confirm-cancel md-button md-ink-ripple"
                  type="button"
                  onClick={() => this.closeEditor(false)}
                >
                  <span className="">Cancel</span>
                </button>
                <button
                  className="modal-window-button confirm-continue md-button md-ink-ripple"
                  type="button"
                  onClick={() => this.closeEditor(true)}
                >
                  <span className="">Confirm</span>
                </button>
              </md-dialog-actions>
            </form>
            <div className="md-dialog-focus-trap" tabIndex={0}></div>
          </md-dialog>
        </CustomModal>
      </div>
    );
  }
}

export default ConversationViewCard;
